import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { calculatePaces, EVENTS, ZONE_ORDER, parseTimeToSeconds, formatTime, applyAltitudeAdjustment } from '../../lib/vdot';
import {
  isRelayEvent,
  getRelayLegOptions,
  defaultRelayLeg,
  relaySplitEvent,
  resultInputLabel,
  getRelayLegFor,
  getEventDate,
  benchmarkEventFor,
} from '../../lib/relays';
import { SEASON_OPTIONS, seasonKeyForBenchmark } from '../../lib/seasons';
import { formatSplitTime } from '../../lib/workoutSegmentParser';
import { resolvePaceTags, renderWorkoutText } from '../../lib/paceTags';
import { TrainingLogModal } from '../athlete/TrainingLogModal';
import { CoachHealthFlagModal } from './CoachHealthFlagModal';
import logoColor from '../../assets/White Border Logo.png';

function formatSegmentActual(seg) {
  if (!seg) return '';
  const splits = (seg.splits ?? []).filter(Boolean);
  switch (seg.type) {
    case 'interval': {
      const unit = seg.distUnit ?? 'm';
      const reps = seg.reps ?? splits.length;
      const repDists = Array.from({ length: reps }, (_, i) =>
        (seg.repDistances?.[i] !== undefined && seg.repDistances[i] !== '') ? seg.repDistances[i] : seg.distance
      );
      const allSame = repDists.every(d => String(d) === String(repDists[0]));
      const prefix = allSame
        ? (reps && repDists[0] ? `${reps}×${repDists[0]}${unit}` : seg.label ?? '')
        : repDists.map(d => `${d}${unit}`).join(', ');
      return splits.length ? `${prefix}: ${splits.join(', ')}` : prefix;
    }
    case 'timed_interval': {
      const reps = seg.reps ?? splits.length;
      let dur = '';
      if (seg.duration) {
        const d = seg.duration;
        if (d < 60) { dur = `${d}"`; }
        else { const m = Math.floor(d / 60); const s = d % 60; dur = s === 0 ? `${m}'` : `${m}:${String(s).padStart(2, '0')}`; }
      }
      const prefix = reps && dur ? `${reps}×${dur}` : seg.label ?? '';
      return splits.length ? `${prefix}: ${splits.join(', ')}` : prefix;
    }
    case 'continuous': {
      if (!seg.distance) return seg.label ?? '';
      const distLabel = `${seg.distance}${seg.distUnit ?? 'mi'}`;
      if (seg.time) {
        const totalSec = parseTimeToSeconds(seg.time);
        const dist = parseFloat(seg.distance);
        if (totalSec && dist) {
          const miles = (seg.distUnit === 'km') ? dist / 1.60934 : (seg.distUnit === 'm') ? dist / 1609.344 : dist;
          const paceSec = totalSec / miles;
          const pm = Math.floor(paceSec / 60);
          const ps = Math.round(paceSec % 60);
          return `${distLabel} (${pm}:${String(ps).padStart(2, '0')}/mi)`;
        }
      }
      return distLabel;
    }
    case 'checkbox':
      return seg.checked ? `✓ ${seg.label ?? ''}` : (seg.label ?? '');
    case 'xt':
      return seg.duration ? `${seg.duration} ${seg.label || 'XT'}` : (seg.label ?? 'XT');
    default:
      return seg.label ?? '';
  }
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const race = new Date(dateStr + 'T00:00:00');
  return Math.round((race - today) / (1000 * 60 * 60 * 24));
}

function formatRaceDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Renders "Apr 12–13, 2026" or "Apr 30 – May 2, 2026" for multi-day
 *  races; falls back to `formatRaceDate` for single-day.
 */
function formatRaceDateRange(startStr, endStr) {
  if (!startStr) return '';
  if (!endStr || endStr === startStr) return formatRaceDate(startStr);
  const start = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  const sameMonth =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    const mon = start.toLocaleDateString('en-US', { month: 'short' });
    return `${mon} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} – ${e}`;
}

function parseRaceEvents(eventField) {
  if (!eventField) return [];
  try {
    const arr = JSON.parse(eventField);
    if (Array.isArray(arr)) return arr;
  } catch {}
  return [eventField];
}

function formatGradeLevel(gl) {
  if (!gl) return '';
  if (gl === 'pro') return 'Pro';
  if (gl === 'post-collegiate') return 'Post-Collegiate';
  return gl;
}

function getEventLabels(eventField) {
  const evts = parseRaceEvents(eventField);
  if (!evts.length) return '';
  return evts.map(e => EVENTS[e]?.label ?? e).join(', ');
}

export function AthleteProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile: coachProfile } = useAuth();
  const isAdmin = coachProfile?.role === 'admin' || coachProfile?.is_admin === true;

  // Remember last viewed athlete for tab navigation
  useEffect(() => {
    if (id) sessionStorage.setItem('apex_last_athlete', id);
  }, [id]);
  const [athlete, setAthlete] = useState(null);
  const [benchmarks, setBenchmarks] = useState([]);
  const [goals, setGoals] = useState([]);
  // null = no form, {} = adding, { id, event, goal_seconds, notes } = editing
  const [goalDraft, setGoalDraft] = useState(null);
  const [goalErr, setGoalErr] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  // Habits — same draft pattern as goals.
  const [habits, setHabits] = useState([]);
  const [habitCompletionsByDate, setHabitCompletionsByDate] = useState({}); // { 'YYYY-MM-DD': Set<habit_id> }
  const [habitDraft, setHabitDraft] = useState(null);
  const [habitErr, setHabitErr] = useState('');
  const [savingHabit, setSavingHabit] = useState(false);
  const [races, setRaces] = useState([]);
  const [paces, setPaces] = useState(null);
  const [vdot, setVdot] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [altitudeFt, setAltitudeFt] = useState(null);
  const [altitudeInput, setAltitudeInput] = useState('');
  const [altitudeSaving, setAltitudeSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Admin-only: athlete info editing
  const [athleteInfoEditing, setAthleteInfoEditing] = useState(false);
  const [athleteInfoForm, setAthleteInfoForm] = useState({});
  const [athleteInfoSaving, setAthleteInfoSaving] = useState(false);
  const [athleteInfoSaved, setAthleteInfoSaved] = useState(false);

  // Athlete avatar upload (coach-side)
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Benchmark selection state
  const [pinnedBenchmarkId, setPinnedBenchmarkId] = useState(null); // coach override
  const [bestBenchmarkId, setBestBenchmarkId] = useState(null);     // auto: highest VDOT

  // Benchmark form state — multi-row bulk entry
  const [benchmarkRows, setBenchmarkRows] = useState([{ event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }]);

  // Race form state
  const [showRaceForm, setShowRaceForm] = useState(false);
  const [editRaceId, setEditRaceId] = useState(null);
  const [raceName, setRaceName] = useState('');
  const [raceFormDate, setRaceFormDate] = useState('');
  const [raceFormDateEnd, setRaceFormDateEnd] = useState('');
  const [raceEvents, setRaceEvents] = useState([]);
  // Per-relay leg distances for multi-relay races. Map of
  // `{ relayEventKey: legMeters }`.
  const [relayLegs, setRelayLegs] = useState({});
  // Per-event dates for multi-day meets — `{ eventKey: 'YYYY-MM-DD' }`.
  // Empty object for single-day races (everyone uses raceFormDate).
  const [eventDates, setEventDates] = useState({});
  // Explicit season tag — 'xc' / 'indoor' / 'outdoor' / '' (auto).
  // Drives improvement-tracker grouping; overrides the date-based
  // heuristic that previously miscategorized boundary races.
  const [raceSeason, setRaceSeason] = useState('');
  const [raceLocation, setRaceLocation] = useState('');
  const [racePriority, setRacePriority] = useState('B');
  const [raceNotes, setRaceNotes] = useState('');
  const [raceSaving, setRaceSaving] = useState(false);
  const [raceError, setRaceError] = useState('');

  // Result logging state
  const [resultInputs, setResultInputs] = useState({});
  const [resultSaving, setResultSaving] = useState(null);
  const [resultErrors, setResultErrors] = useState({});
  const [editingResultId, setEditingResultId] = useState(null);
  const [savedResults, setSavedResults] = useState({});

  // Parents state
  const [parents, setParents] = useState([]);
  const [parentEmail, setParentEmail] = useState('');
  const [parentSaving, setParentSaving] = useState(false);
  const [parentError, setParentError] = useState('');

  // Vacation state
  const [vacations, setVacations] = useState([]);
  const [showVacForm, setShowVacForm] = useState(false);
  const [vacTitle, setVacTitle] = useState('');
  const [vacStart, setVacStart] = useState('');
  const [vacEnd, setVacEnd] = useState('');
  const [vacEquipment, setVacEquipment] = useState([]);
  const [vacBlackouts, setVacBlackouts] = useState([]);
  const [vacNotes, setVacNotes] = useState('');
  const [vacSaving, setVacSaving] = useState(false);
  const [vacError, setVacError] = useState('');

  // Comments state
  const [athleteComments, setAthleteComments] = useState([]);

  // Training log calendar state
  const now = new Date();
  const [logMonth, setLogMonth] = useState(now.getMonth());
  const [logYear, setLogYear] = useState(now.getFullYear());
  const [monthLogs, setMonthLogs] = useState({}); // { 'YYYY-MM-DD': mergedLog }
  const [monthLogSessions, setMonthLogSessions] = useState({}); // { 'YYYY-MM-DD': { am, pm, full } }
  const [planWorkouts, setPlanWorkouts] = useState({}); // { 'YYYY-MM-DD': text }
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [monthFlags, setMonthFlags] = useState([]); // health flag rows overlapping month
  const [logViewDay, setLogViewDay] = useState(null); // { date, log, planned }
  const [logEditDay, setLogEditDay] = useState(null); // for TrainingLogModal
  const [editingFlag, setEditingFlag] = useState(null); // health_flags row currently being edited
  const [creatingFlagDate, setCreatingFlagDate] = useState(null); // 'YYYY-MM-DD' when adding a new flag

  // When monthFlags changes (after a flag edit / delete) keep the
  // open day modal in sync. Otherwise the row would still show
  // its pre-edit values until the coach closes + reopens the day.
  useEffect(() => {
    if (!logViewDay) return;
    const map = buildFlagDateMap(monthFlags);
    const fresh = map[logViewDay.date] ?? [];
    setLogViewDay(prev => prev ? { ...prev, flags: fresh } : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFlags]);

  function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  function goToPrevMonth() {
    if (logMonth === 0) { setLogMonth(11); setLogYear(y => y - 1); }
    else setLogMonth(m => m - 1);
  }

  function goToNextMonth() {
    if (logMonth === 11) { setLogMonth(0); setLogYear(y => y + 1); }
    else setLogMonth(m => m + 1);
  }

  function buildCalendarCells(year, month) {
    const rawFirstDay = new Date(year, month, 1).getDay();
    const firstDay = (rawFirstDay + 6) % 7; // convert Sun=0…Sat=6 → Mon=0…Sun=6
    const daysInMonth = getDaysInMonth(year, month);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    const monthStr = String(month + 1).padStart(2, '0');
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${year}-${monthStr}-${String(d).padStart(2, '0')}`);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  function fmtDelta(seconds) {
    const total = Math.round(Math.abs(seconds));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
  }

  function avgPaceFromSeg(seg) {
    if (seg.type === 'continuous') {
      const dist = parseFloat(seg.distance);
      if (!dist || !seg.time) return null;
      const parts = String(seg.time).split(':').map(Number);
      const totalSec = parts.length === 3 ? parts[0] * 3600 + parts[1] * 60 + parts[2]
        : parts.length === 2 ? parts[0] * 60 + parts[1] : parseFloat(parts[0]);
      if (!totalSec) return null;
      const miles = seg.distUnit === 'mi' ? dist : seg.distUnit === 'km' ? dist / 1.60934 : dist / 1609.344;
      const paceSec = totalSec / miles;
      const m = Math.floor(paceSec / 60);
      const s = Math.round(paceSec % 60);
      return `${m}:${String(s).padStart(2, '0')}/mi`;
    }
    if (seg.type === 'interval') {
      const splits = (seg.splits ?? []).filter(s => s !== '');
      if (!splits.length || !seg.distance) return null;
      const totalSec = splits.reduce((acc, sp) => {
        const p = String(sp).split(':').map(Number);
        return acc + (p.length === 2 ? p[0] * 60 + p[1] : parseFloat(p[0]));
      }, 0);
      const avgSec = totalSec / splits.length;
      const dist = parseFloat(seg.distance);
      const meters = seg.distUnit === 'mi' ? dist * 1609.344 : seg.distUnit === 'km' ? dist * 1000 : dist;
      const paceSec = (avgSec / meters) * 1609.344;
      const m = Math.floor(paceSec / 60);
      const s = Math.round(paceSec % 60);
      return `${m}:${String(s).padStart(2, '0')}/mi`;
    }
    return null;
  }

  useEffect(() => {
    fetchAthlete();
    fetchBenchmarks();
    fetchRaces();
    fetchGoals();
    fetchHabits();
    fetchParents();
    fetchVacations();
    fetchComments();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchMonthLogs();
    fetchPlanWorkouts();
    fetchMonthFlags();
    fetchHabitCompletionsForMonth();
  }, [id, logMonth, logYear]);

  // Recompute paces whenever benchmarks list or pin changes
  useEffect(() => {
    recalcPaces(benchmarks, pinnedBenchmarkId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarks, pinnedBenchmarkId]);

  // Pre-populate savedResults from benchmarks for all races with
  // known events. Lookup keys use the saved event (split for relays)
  // and the per-event date from event_dates (multi-day meets).
  useEffect(() => {
    if (!races.length || !benchmarks.length) return;
    const prefill = {};
    races.forEach(race => {
      const events = parseRaceEvents(race.event);
      if (events.length === 0) return;
      events.forEach(ek => {
        const bmEvent = benchmarkEventFor(race, ek);
        const bmDate = getEventDate(race, ek);
        const bm =
          benchmarks.find(b => b.event === bmEvent && b.race_date === bmDate && b.notes === race.name) ||
          benchmarks.find(b => b.event === bmEvent && b.race_date === bmDate);
        if (bm) prefill[`${race.id}-${ek}`] = formatTime(bm.time_seconds);
      });
    });
    if (Object.keys(prefill).length) {
      setSavedResults(prev => ({ ...prefill, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [races, benchmarks]);

  async function fetchRaces() {
    const { data } = await supabase
      .from('races')
      .select('*')
      .eq('athlete_id', id)
      .order('race_date', { ascending: true });
    setRaces(data ?? []);
  }

  function resetRaceForm() {
    setEditRaceId(null); setRaceName(''); setRaceFormDate(''); setRaceFormDateEnd('');
    setRaceEvents([]); setRelayLegs({}); setEventDates({}); setRaceSeason('');
    setRaceLocation(''); setRacePriority('B'); setRaceNotes('');
    setShowRaceForm(false);
  }

  /**
   * Checkbox handler for the race-form event grid. Multiple relays
   * per race are supported — each relay's leg lives in `relayLegs`
   * keyed by the relay event.
   */
  function toggleRaceEventCheckbox(key, checked) {
    const relay = isRelayEvent(key);
    setRaceEvents(prev => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter(p => p !== key);
    });
    if (relay) {
      setRelayLegs(prev => {
        const next = { ...prev };
        if (checked) {
          if (next[key] == null) next[key] = String(defaultRelayLeg(key) ?? '');
        } else {
          delete next[key];
        }
        return next;
      });
    }
  }

  async function handleAddRace(e) {
    e.preventDefault();
    setRaceError('');
    setRaceSaving(true);
    const relayLegsForSave = {};
    for (const ek of raceEvents) {
      if (isRelayEvent(ek) && relayLegs[ek]) relayLegsForSave[ek] = relayLegs[ek];
    }
    // For multi-day meets, persist per-event dates. Drop entries that
    // match the start date — those don't add information and would
    // bloat the JSON.
    const isMultiDay = raceFormDateEnd && raceFormDateEnd !== raceFormDate;
    const eventDatesForSave = {};
    if (isMultiDay) {
      for (const ek of raceEvents) {
        const d = eventDates[ek];
        if (d && d !== raceFormDate) eventDatesForSave[ek] = d;
      }
    }
    const payload = {
      name: raceName,
      race_date: raceFormDate,
      race_date_end: isMultiDay ? raceFormDateEnd : null,
      event: raceEvents.length > 0 ? JSON.stringify(raceEvents) : null,
      relay_legs: Object.keys(relayLegsForSave).length ? relayLegsForSave : null,
      relay_leg: null,
      event_dates: Object.keys(eventDatesForSave).length ? eventDatesForSave : null,
      season: raceSeason || null,
      location: raceLocation || null,
      priority: racePriority,
      notes: raceNotes || null,
    };
    let err;
    if (editRaceId) {
      ({ error: err } = await supabase.from('races').update(payload).eq('id', editRaceId));
    } else {
      ({ error: err } = await supabase.from('races').insert({ athlete_id: id, ...payload }));
    }
    if (err) { setRaceError(err.message); setRaceSaving(false); return; }
    resetRaceForm();
    await fetchRaces();
    setRaceSaving(false);
  }

  async function handleDeleteRace(raceId) {
    await supabase.from('races').delete().eq('id', raceId);
    setRaces(prev => prev.filter(r => r.id !== raceId));
  }

  async function handleLogResult(race, eventKey) {
    const inputKey = eventKey ? `${race.id}-${eventKey}` : race.id;
    const seconds = parseTimeToSeconds(resultInputs[inputKey] ?? '');
    if (!seconds) {
      setResultErrors(prev => ({ ...prev, [inputKey]: 'Enter a valid time (e.g. 4:32.56 or 16:45.00)' }));
      return;
    }
    setResultErrors(prev => ({ ...prev, [inputKey]: '' }));
    setResultSaving(race.id);

    const events = parseRaceEvents(race.event);
    const isPrimary = !eventKey || eventKey === events[0];

    if (isPrimary) {
      await supabase.from('races').update({ result_time_seconds: seconds }).eq('id', race.id);
    }

    // Always write a benchmark — use the explicit eventKey or fall back to the race's first event.
    // For multi-day meets, benchmark.race_date is the event-specific
    // date from `event_dates`, not the meet's start date. Single-day
    // races fall through to race.race_date.
    const rawBenchmarkEvent = eventKey || events[0];
    const benchmarkEvent = rawBenchmarkEvent
      ? benchmarkEventFor(race, rawBenchmarkEvent)
      : null;
    const benchmarkDate = rawBenchmarkEvent
      ? getEventDate(race, rawBenchmarkEvent)
      : race.race_date;
    if (benchmarkEvent) {
      // Replace any existing benchmark for this slot so re-saves
      // don't pile up duplicate rows.
      await supabase.from('benchmarks')
        .delete()
        .eq('athlete_id', id)
        .eq('event', benchmarkEvent)
        .eq('race_date', benchmarkDate)
        .eq('notes', race.name);
      await supabase.from('benchmarks').insert({
        athlete_id: id,
        event: benchmarkEvent,
        time_seconds: seconds,
        race_date: benchmarkDate,
        notes: race.name,
        season: race.season ?? null,
      });
    }

    setSavedResults(prev => ({ ...prev, [inputKey]: formatTime(seconds) }));
    setResultInputs(prev => ({ ...prev, [inputKey]: '' }));
    setResultSaving(null);
    setEditingResultId(null);
    await fetchRaces();
    await fetchBenchmarks();
  }

  async function handleLogAllResults(race) {
    const events = parseRaceEvents(race.event);
    // Rely on savedResults (pre-filled from benchmarks) to detect already-logged events,
    // rather than using race.result_time_seconds as a proxy — that caused single-event races
    // to be skipped when result_time_seconds was set without a matching benchmark row.
    const unsaved = events.filter(ek => !savedResults[`${race.id}-${ek}`]);

    // Validate filled-in inputs
    const newErrors = {};
    for (const ek of unsaved) {
      const inputKey = `${race.id}-${ek}`;
      const val = resultInputs[inputKey] ?? '';
      if (val.trim() && !parseTimeToSeconds(val)) {
        newErrors[inputKey] = 'Invalid time format';
      }
    }
    if (Object.keys(newErrors).length) {
      setResultErrors(prev => ({ ...prev, ...newErrors }));
      return;
    }

    setResultSaving(race.id);
    setResultErrors({});

    const toSave = unsaved.filter(ek => (resultInputs[`${race.id}-${ek}`] ?? '').trim() !== '');

    for (const ek of toSave) {
      const inputKey = `${race.id}-${ek}`;
      const seconds = parseTimeToSeconds(resultInputs[inputKey]);
      if (!seconds) continue;

      if (ek === events[0]) {
        await supabase.from('races').update({ result_time_seconds: seconds }).eq('id', race.id);
      }

      // Same dedupe logic as handleLogResult — delete any existing
      // (athlete, event, eventDate, race_name) row before inserting
      // so re-saves don't pile up duplicate rows. Multi-day meets
      // resolve the date per-event from `event_dates`.
      const bmEvent = benchmarkEventFor(race, ek);
      const bmDate = getEventDate(race, ek);
      await supabase.from('benchmarks')
        .delete()
        .eq('athlete_id', id)
        .eq('event', bmEvent)
        .eq('race_date', bmDate)
        .eq('notes', race.name);
      await supabase.from('benchmarks').insert({
        athlete_id: id,
        event: bmEvent,
        time_seconds: seconds,
        race_date: bmDate,
        notes: race.name,
        season: race.season ?? null,
      });

      setSavedResults(prev => ({ ...prev, [inputKey]: formatTime(seconds) }));
    }

    setResultInputs(prev => {
      const next = { ...prev };
      toSave.forEach(ek => delete next[`${race.id}-${ek}`]);
      return next;
    });
    setResultSaving(null);
    await fetchRaces();
    await fetchBenchmarks();
  }

  function renderResultInputs(race) {
    const events = parseRaceEvents(race.event);

    if (events.length === 0) {
      const inputKey = race.id;
      const alreadySaved = race.result_time_seconds || savedResults[inputKey];
      if (alreadySaved && editingResultId !== race.id) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{savedResults[inputKey] ?? formatTime(race.result_time_seconds)}</div>
            <button className="btn-xs btn-xs-ghost" style={{ fontSize: 11 }}
              onClick={() => { setEditingResultId(race.id); setResultInputs(prev => ({ ...prev, [inputKey]: formatTime(race.result_time_seconds) })); }}>Edit</button>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input className="form-input" style={{ width: 90, padding: '5px 8px', fontSize: 12 }}
              type="text" placeholder="4:32.56"
              value={resultInputs[inputKey] ?? ''}
              onChange={e => setResultInputs(prev => ({ ...prev, [inputKey]: e.target.value }))} />
            <button className="btn-xs btn-xs-primary"
              onClick={() => handleLogResult(race, null)}
              disabled={resultSaving === race.id}>
              {resultSaving === race.id ? '...' : 'Save'}
            </button>
          </div>
          {resultErrors[inputKey] && <div style={{ fontSize: 11, color: 'var(--red, #dc2626)' }}>{resultErrors[inputKey]}</div>}
        </div>
      );
    }

    // Multi-event: inputs together, one Save button
    const allSaved = events.every((ek, i) => {
      const inputKey = `${race.id}-${ek}`;
      return (savedResults[inputKey] && editingResultId !== inputKey) || (i === 0 && race.result_time_seconds && editingResultId !== inputKey);
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {events.map((eventKey, i) => {
          const inputKey = `${race.id}-${eventKey}`;
          const savedTime = savedResults[inputKey] ?? (i === 0 && race.result_time_seconds ? formatTime(race.result_time_seconds) : null);
          // Show the split label ("800m (split)") on relay inputs so
          // it matches what the benchmark gets saved as.
          const label = resultInputLabel(eventKey, getRelayLegFor(race, eventKey), EVENTS);
          if (savedTime && editingResultId !== inputKey) {
            return (
              <div key={eventKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-400)', minWidth: 68, flexShrink: 0 }}>{label}:</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{savedTime}</span>
                <button className="btn-xs btn-xs-ghost" style={{ fontSize: 11 }}
                  onClick={() => { setEditingResultId(inputKey); setResultInputs(prev => ({ ...prev, [inputKey]: savedTime })); }}>Edit</button>
              </div>
            );
          }
          return (
            <div key={eventKey} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-400)', minWidth: 68, flexShrink: 0 }}>{label}:</span>
                <input className="form-input" style={{ width: 90, padding: '5px 8px', fontSize: 12 }}
                  type="text" placeholder="4:32.56"
                  value={resultInputs[inputKey] ?? ''}
                  onChange={e => setResultInputs(prev => ({ ...prev, [inputKey]: e.target.value }))} />
                {editingResultId === inputKey && (
                  <button className="btn-xs btn-xs-primary"
                    onClick={() => handleLogResult(race, eventKey)}
                    disabled={resultSaving === race.id}>
                    {resultSaving === race.id ? '...' : 'Save'}
                  </button>
                )}
              </div>
              {resultErrors[inputKey] && <div style={{ fontSize: 11, color: 'var(--red, #dc2626)', paddingLeft: 62 }}>{resultErrors[inputKey]}</div>}
            </div>
          );
        })}
        {!allSaved && (
          <button className="btn-xs btn-xs-primary" style={{ alignSelf: 'flex-start', marginTop: 2 }}
            onClick={() => handleLogAllResults(race)}
            disabled={resultSaving === race.id}>
            {resultSaving === race.id ? 'Saving...' : 'Save Results'}
          </button>
        )}
      </div>
    );
  }

  async function fetchAthlete() {
    const { data: profile } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('id', id)
      .single();

    const { data: ap, error: apErr } = await supabase
      .from('athlete_profiles')
      .select('*')
      .eq('id', id)
      .single();

    setAthlete({ ...profile, ...ap });
    setPinnedBenchmarkId(ap?.pinned_benchmark_id ?? null);
    const ft = ap?.training_altitude_ft ?? null;
    setAltitudeFt(ft);
    setAltitudeInput(ft != null ? String(ft) : '');
    setLoading(false);
  }

  async function fetchBenchmarks() {
    const { data } = await supabase
      .from('benchmarks')
      .select('*')
      .eq('athlete_id', id)
      .order('race_date', { ascending: false });

    setBenchmarks(data ?? []);
    // recalcPaces is triggered by the benchmarks/pinnedBenchmarkId useEffect
  }

  async function fetchGoals() {
    // Coach manages active goals only — achieved goals are
    // history (the athlete celebrates + clears them on the
    // dashboard). Selecting id so we can update / delete by row.
    const { data } = await supabase
      .from('athlete_goals')
      .select('id, event, goal_seconds, notes')
      .eq('athlete_id', id)
      .is('achieved_at', null);
    const sorted = (data ?? []).sort((a, b) => {
      const ea = EVENTS[a.event]?.meters ?? Infinity;
      const eb = EVENTS[b.event]?.meters ?? Infinity;
      return ea - eb;
    });
    setGoals(sorted);
  }

  async function saveGoalDraft() {
    setGoalErr('');
    const draft = goalDraft ?? {};
    if (!draft.event) { setGoalErr('Pick an event'); return; }
    const seconds = parseTimeToSeconds(draft.goal_time_str ?? '');
    if (!seconds || seconds <= 0) {
      setGoalErr('Enter a valid goal time (e.g. 3:55.00 or 15:30)');
      return;
    }
    // Block duplicate active goal for the same event unless editing
    // the existing one.
    if (
      goals.some(g => g.event === draft.event && g.id !== draft.id)
    ) {
      setGoalErr('There\'s already an active goal for that event. Edit or delete it first.');
      return;
    }
    setSavingGoal(true);
    const payload = {
      athlete_id: id,
      event: draft.event,
      goal_seconds: seconds,
      notes: draft.notes?.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const { error } = draft.id
      ? await supabase.from('athlete_goals').update(payload).eq('id', draft.id)
      : await supabase.from('athlete_goals').insert(payload);
    setSavingGoal(false);
    if (error) {
      setGoalErr(error.message);
      return;
    }
    setGoalDraft(null);
    await fetchGoals();
  }

  async function deleteGoal(goalId) {
    if (!window.confirm('Delete this goal?')) return;
    const { error } = await supabase.from('athlete_goals').delete().eq('id', goalId);
    if (error) {
      window.alert(`Could not delete: ${error.message}`);
      return;
    }
    await fetchGoals();
  }

  // ── Habits ──────────────────────────────────────────────────────
  async function fetchHabits() {
    const { data } = await supabase
      .from('athlete_habits')
      .select('id, name, days_of_week, active, notes, created_at')
      .eq('athlete_id', id)
      .eq('active', true)
      .order('created_at', { ascending: true });
    setHabits(data ?? []);
  }

  async function fetchHabitCompletionsForMonth() {
    const monthStr = String(logMonth + 1).padStart(2, '0');
    const start = `${logYear}-${monthStr}-01`;
    const last = String(getDaysInMonth(logYear, logMonth)).padStart(2, '0');
    const end = `${logYear}-${monthStr}-${last}`;
    const { data } = await supabase
      .from('athlete_habit_completions')
      .select('habit_id, completion_date')
      .eq('athlete_id', id)
      .gte('completion_date', start)
      .lte('completion_date', end);
    const map = {};
    (data ?? []).forEach(row => {
      const d = row.completion_date;
      if (!map[d]) map[d] = new Set();
      map[d].add(row.habit_id);
    });
    setHabitCompletionsByDate(map);
  }

  async function saveHabitDraft() {
    setHabitErr('');
    const draft = habitDraft ?? {};
    if (!draft.name?.trim()) { setHabitErr('Give the habit a name.'); return; }
    const days = (draft.days_of_week ?? []).slice().sort((a, b) => a - b);
    if (!days.length) { setHabitErr('Pick at least one day.'); return; }
    setSavingHabit(true);
    const payload = {
      athlete_id: id,
      name: draft.name.trim(),
      days_of_week: days,
      notes: draft.notes?.trim() || null,
      active: true,
      updated_at: new Date().toISOString(),
    };
    const { error } = draft.id
      ? await supabase.from('athlete_habits').update(payload).eq('id', draft.id)
      : await supabase.from('athlete_habits').insert({ ...payload, created_by: coachProfile?.id ?? null });
    setSavingHabit(false);
    if (error) {
      setHabitErr(error.message);
      return;
    }
    setHabitDraft(null);
    await fetchHabits();
  }

  async function deleteHabit(habitId) {
    if (!window.confirm('Delete this habit? Past completions are removed too.')) return;
    const { data, error } = await supabase
      .from('athlete_habits')
      .delete()
      .eq('id', habitId)
      .select();
    if (error) {
      window.alert(`Could not delete: ${error.message}`);
      return;
    }
    if (!data?.length) {
      window.alert("Couldn't delete — your account may not have permission.");
      return;
    }
    await fetchHabits();
    await fetchHabitCompletionsForMonth();
  }

  function recalcPaces(bmarks, pinnedId) {
    if (!bmarks || bmarks.length === 0) {
      setPaces(null); setVdot(null); setBestBenchmarkId(null); return;
    }
    // Find the benchmark with the highest VDOT across all events
    let bestBmark = null;
    let bestVdotVal = -Infinity;
    for (const b of bmarks) {
      const result = calculatePaces(b.event, b.time_seconds);
      if (result && result.vdot > bestVdotVal) {
        bestVdotVal = result.vdot;
        bestBmark = b;
      }
    }
    setBestBenchmarkId(bestBmark?.id ?? null);
    // Coach override takes precedence; fall back to best VDOT
    const pinnedBmark = pinnedId ? bmarks.find(b => b.id === pinnedId) : null;
    const active = pinnedBmark ?? bestBmark;
    const result = active ? calculatePaces(active.event, active.time_seconds) : null;
    if (result) { setPaces(result.paces); setVdot(result.vdot); }
    else { setPaces(null); setVdot(null); }
  }

  async function handleAddBenchmarks(e) {
    e.preventDefault();
    setError('');
    const valid = benchmarkRows.filter(r => r.time.trim());
    if (valid.length === 0) { setError('Enter at least one time.'); return; }
    setSaving(true);
    for (const row of valid) {
      const seconds = parseTimeToSeconds(row.time);
      if (!seconds) continue;
      await supabase.from('benchmarks').insert({
        athlete_id: id,
        event: row.event === 'other'
          ? (row.customDist ? `${parseFloat(row.customDist).toFixed(1)} ${row.customUnit}` : row.event)
          : row.event,
        time_seconds: seconds,
        race_date: row.date || null,
      });
    }
    setBenchmarkRows([{ event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }]);
    await fetchBenchmarks();
    setSaving(false);
  }

  async function handleDeleteBenchmark(benchmarkId) {
    // If deleting the pinned benchmark, clear the pin first
    if (pinnedBenchmarkId === benchmarkId) {
      await supabase.from('athlete_profiles').update({ pinned_benchmark_id: null }).eq('id', id);
      setPinnedBenchmarkId(null);
    }
    await supabase.from('benchmarks').delete().eq('id', benchmarkId);
    await fetchBenchmarks();
  }

  async function handleSaveAthleteInfo() {
    setAthleteInfoSaving(true);
    setAthleteInfoSaved(false);
    const patch = {
      id,
      school_name: athleteInfoForm.school_name || null,
      grade_level: athleteInfoForm.grade_level || null,
      gender: athleteInfoForm.gender || null,
      birth_date: athleteInfoForm.birth_date || null,
      phone: athleteInfoForm.phone || null,
      shirt_size: athleteInfoForm.shirt_size || null,
      address: athleteInfoForm.address || null,
    };
    await supabase.from('athlete_profiles').upsert(patch, { onConflict: 'id' });
    setAthlete(prev => ({ ...prev, ...patch }));
    setAthleteInfoSaving(false);
    setAthleteInfoSaved(true);
    setAthleteInfoEditing(false);
    setTimeout(() => setAthleteInfoSaved(false), 2500);
  }

  async function handleSaveAltitude() {
    setAltitudeSaving(true);
    const ft = altitudeInput.trim() ? parseInt(altitudeInput, 10) : null;
    await supabase.from('athlete_profiles').update({ training_altitude_ft: ft }).eq('id', id);
    setAltitudeFt(ft);
    setAltitudeSaving(false);
  }

  async function handlePinBenchmark(benchmarkId) {
    const { error: err } = await supabase.from('athlete_profiles').update({ pinned_benchmark_id: benchmarkId }).eq('id', id);
    if (err) { setError('Failed to save pace override: ' + err.message); return; }
    setPinnedBenchmarkId(benchmarkId);
  }

  async function handleClearPin() {
    const { error: err } = await supabase.from('athlete_profiles').update({ pinned_benchmark_id: null }).eq('id', id);
    if (err) { setError('Failed to clear pace override: ' + err.message); return; }
    setPinnedBenchmarkId(null);
  }

  async function handleCoachAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${id}.${ext}`;
    const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
    if (upErr) { console.error('[avatar upload] storage error:', upErr.message); setAvatarUploading(false); return; }
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    const url = `${urlData.publicUrl}?t=${Date.now()}`;
    const { error: dbErr } = await supabase.from('athlete_profiles').update({ avatar_url: url }).eq('id', id);
    if (dbErr) console.error('[avatar upload] db error:', dbErr.message);
    setAthlete(prev => ({ ...prev, avatar_url: url }));
    setAvatarUploading(false);
  }

  async function fetchParents() {
    const { data } = await supabase
      .from('parent_athlete_links')
      .select('id, parent_id, profiles!parent_athlete_links_parent_id_fkey(full_name, email)')
      .eq('athlete_id', id);
    setParents(data ?? []);
  }

  async function handleAddParent(e) {
    e.preventDefault();
    setParentError('');
    setParentSaving(true);

    const { data: found } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', parentEmail.trim().toLowerCase())
      .eq('role', 'parent')
      .maybeSingle();

    if (!found) {
      setParentError('No parent account found with that email. They must sign up first with role "parent".');
      setParentSaving(false);
      return;
    }

    const already = parents.find(p => p.parent_id === found.id);
    if (already) {
      setParentError('This parent is already linked.');
      setParentSaving(false);
      return;
    }

    const { error: err } = await supabase.from('parent_athlete_links').insert({
      parent_id: found.id,
      athlete_id: id,
    });

    if (err) { setParentError(err.message); setParentSaving(false); return; }

    setParentEmail('');
    await fetchParents();
    setParentSaving(false);
  }

  async function handleRemoveParent(linkId) {
    await supabase.from('parent_athlete_links').delete().eq('id', linkId);
    setParents(prev => prev.filter(p => p.id !== linkId));
  }

  // ── Monthly plan PDF download ─────────────────────────────────────
  async function downloadMonthlyPlanPDF() {
    setPdfGenerating(true);
    try {
      const { jsPDF } = await import('jspdf');
      const month = logMonth + 1;
      const year = logYear;
      const monthName = new Date(year, logMonth, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
      const lastDay = new Date(year, month, 0).getDate();
      const monthStr = String(month).padStart(2, '0');
      const resolvedMap = {};
      const adjustedPaces = applyAltitudeAdjustment(paces, altitudeFt);
      for (const [date, entry] of Object.entries(planWorkouts)) {
        resolvedMap[date] = resolvePaceTags(entry?.text ?? entry ?? '', adjustedPaces);
      }
      const firstDow = (new Date(year, logMonth, 1).getDay() + 6) % 7;
      const cells = [];
      for (let i = 0; i < firstDow; i++) cells.push(null);
      for (let d = 1; d <= lastDay; d++) {
        const dateStr = `${year}-${monthStr}-${String(d).padStart(2, '0')}`;
        cells.push({ day: d, date: dateStr, workout: resolvedMap[dateStr] || '' });
      }
      while (cells.length % 7 !== 0) cells.push(null);
      const weeks = [];
      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
      const PW = 279.4, PH = 215.9, ML = 10, MR = 10;
      const CW = PW - ML - MR;
      const COL_W = CW / 7;
      const NAVY = [15, 23, 42], GOLD = [180, 143, 60];
      const GRAY_BG = [247, 248, 250], GRAY_LINE = [210, 214, 220], GRAY_TEXT = [150, 155, 165];
      const TODAY_BG = [255, 252, 235];
      const HDR_H = 22, DAY_HDR_H = 6, FOOTER_H = 10;
      const CAL_Y = HDR_H + DAY_HDR_H;
      const CAL_H = PH - CAL_Y - FOOTER_H;
      const ROW_H = CAL_H / weeks.length;
      const CELL_PAD = 1.0, NUM_H = 3.5, LINE_H = 2.2, FONT_SZ = 5.5;
      const maxLines = Math.floor((ROW_H - NUM_H - CELL_PAD * 2) / LINE_H);

      doc.setFillColor(...NAVY);
      doc.rect(0, 0, PW, HDR_H, 'F');
      try {
        const { dataUrl: logoDataUrl, imgW, imgH } = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const MAX_PX = 600;
            const scale = Math.min(MAX_PX / img.naturalWidth, MAX_PX / img.naturalHeight, 1);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.naturalWidth * scale);
            canvas.height = Math.round(img.naturalHeight * scale);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve({ dataUrl: canvas.toDataURL('image/png'), imgW: img.naturalWidth, imgH: img.naturalHeight });
          };
          img.onerror = reject;
          img.src = logoColor;
        });
        const MAX_LOGO_W = 44, MAX_LOGO_H = 17;
        const ratio = imgW / imgH;
        let LOGO_W = MAX_LOGO_W, LOGO_H = LOGO_W / ratio;
        if (LOGO_H > MAX_LOGO_H) { LOGO_H = MAX_LOGO_H; LOGO_W = LOGO_H * ratio; }
        doc.addImage(logoDataUrl, 'PNG', ML, (HDR_H - LOGO_H) / 2, LOGO_W, LOGO_H);
      } catch {
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('APEX ELITE', ML, 11);
      }
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(monthName.toUpperCase(), PW - MR, 11, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.text(athlete?.full_name ?? '', PW - MR, 17, { align: 'right' });
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.6);
      doc.line(0, HDR_H, PW, HDR_H);
      const DAY_NAMES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
      doc.setFillColor(...GRAY_BG);
      doc.rect(ML, HDR_H, CW, DAY_HDR_H, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...NAVY);
      DAY_NAMES.forEach((name, i) => {
        doc.text(name, ML + i * COL_W + COL_W / 2, HDR_H + 4.7, { align: 'center' });
      });
      const todayForPdf = new Date().toISOString().slice(0, 10);
      doc.setLineWidth(0.2);
      weeks.forEach((week, wi) => {
        const rowY = CAL_Y + wi * ROW_H;
        week.forEach((cell, ci) => {
          const cellX = ML + ci * COL_W;
          if (!cell) {
            doc.setFillColor(...GRAY_BG);
            doc.rect(cellX, rowY, COL_W, ROW_H, 'F');
          } else if (cell.date === todayForPdf) {
            doc.setFillColor(...TODAY_BG);
            doc.rect(cellX, rowY, COL_W, ROW_H, 'F');
            doc.setDrawColor(...GOLD);
            doc.setLineWidth(1);
            doc.line(cellX, rowY, cellX, rowY + ROW_H);
            doc.setLineWidth(0.2);
          }
          doc.setDrawColor(...GRAY_LINE);
          doc.setLineWidth(0.2);
          doc.rect(cellX, rowY, COL_W, ROW_H, 'S');
          if (!cell) return;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...NAVY);
          doc.text(String(cell.day), cellX + CELL_PAD, rowY + NUM_H);
          const textY = rowY + NUM_H + LINE_H;
          if (cell.workout) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(FONT_SZ);
            doc.setTextColor(40, 45, 55);
            const allLines = doc.splitTextToSize(cell.workout, COL_W - CELL_PAD * 2);
            const visibleLines = allLines.slice(0, maxLines);
            if (allLines.length > maxLines) {
              visibleLines[maxLines - 1] = visibleLines[maxLines - 1].replace(/\s*\S+$/, '') + '…';
            }
            doc.text(visibleLines, cellX + CELL_PAD, textY);
          } else {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(6.5);
            doc.setTextColor(...GRAY_TEXT);
            doc.text('Rest', cellX + CELL_PAD, textY);
          }
        });
      });
      doc.setDrawColor(...NAVY);
      doc.setLineWidth(0.4);
      doc.line(ML, CAL_Y + weeks.length * ROW_H, ML + CW, CAL_Y + weeks.length * ROW_H);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...GRAY_TEXT);
      const genDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      doc.text(`Generated ${genDate} · Apex Elite`, ML, PH - 4);
      doc.text(paces ? 'Paces calibrated to Apex Elite proprietary training zones' : 'No benchmark — raw pace tags shown', PW - MR, PH - 4, { align: 'right' });
      doc.save(`apex-elite-${athlete?.full_name?.replace(/\s+/g, '-') ?? 'athlete'}-${year}-${monthStr}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert(`PDF generation failed: ${err?.message ?? err}`);
    } finally {
      setPdfGenerating(false);
    }
  }

  async function fetchVacations() {
    const { data } = await supabase
      .from('athlete_vacations')
      .select('id, title, start_date, end_date, available_equipment, blackout_dates, notes')
      .eq('athlete_id', id)
      .order('start_date', { ascending: true });
    setVacations(data ?? []);
  }

  async function handleAddVacation(e) {
    e.preventDefault();
    setVacError('');
    if (!vacStart || !vacEnd) { setVacError('Please enter start and end dates.'); return; }
    if (vacEnd < vacStart) { setVacError('End date must be on or after start date.'); return; }
    setVacSaving(true);
    const { error: err } = await supabase.from('athlete_vacations').insert({
      athlete_id: id,
      title: vacTitle.trim() || null,
      start_date: vacStart,
      end_date: vacEnd,
      available_equipment: vacEquipment,
      blackout_dates: vacBlackouts,
      notes: vacNotes.trim() || null,
    });
    if (err) { setVacError(err.message); setVacSaving(false); return; }
    setVacTitle(''); setVacStart(''); setVacEnd(''); setVacEquipment([]); setVacBlackouts([]); setVacNotes('');
    setShowVacForm(false);
    await fetchVacations();
    setVacSaving(false);
  }

  async function handleDeleteVacation(vacId) {
    await supabase.from('athlete_vacations').delete().eq('id', vacId);
    setVacations(prev => prev.filter(v => v.id !== vacId));
  }

  async function fetchComments() {
    const { data } = await supabase
      .from('workout_comments')
      .select('id, comment_date, text')
      .eq('athlete_id', id)
      .order('comment_date', { ascending: false })
      .limit(30);
    setAthleteComments(data ?? []);
  }

  async function fetchMonthLogs() {
    const monthStr = String(logMonth + 1).padStart(2, '0');
    const start = `${logYear}-${monthStr}-01`;
    const end = `${logYear}-${monthStr}-${String(getDaysInMonth(logYear, logMonth)).padStart(2, '0')}`;
    const { data } = await supabase
      .from('training_logs')
      .select('id, log_date, total_miles, running_miles, xt_miles, segments, notes, session')
      .eq('athlete_id', id)
      .gte('log_date', start)
      .lte('log_date', end);
    const sessions = {};
    for (const row of data ?? []) {
      const date = row.log_date;
      const sess = row.session ?? 'full';
      if (!sessions[date]) sessions[date] = {};
      const existing = sessions[date][sess];
      if (!existing || row.id > existing.id) sessions[date][sess] = row;
    }
    const merged = {};
    for (const [date, dateSessions] of Object.entries(sessions)) {
      for (const row of Object.values(dateSessions)) {
        if (!merged[date]) merged[date] = { segments: [], total_miles: 0, running_miles: 0, xt_miles: 0, notes: row.notes };
        merged[date].segments = [...merged[date].segments, ...(row.segments ?? [])];
        merged[date].total_miles += row.total_miles ?? 0;
        merged[date].running_miles += row.running_miles ?? row.total_miles ?? 0;
        merged[date].xt_miles += row.xt_miles ?? 0;
      }
    }
    setMonthLogs(merged);
    setMonthLogSessions(sessions);
  }

  async function fetchPlanWorkouts() {
    const monthStr = String(logMonth + 1).padStart(2, '0');
    const start = `${logYear}-${monthStr}-01`;
    const end = `${logYear}-${monthStr}-${String(getDaysInMonth(logYear, logMonth)).padStart(2, '0')}`;
    // Find plan assigned to this athlete for this month (any coach, pick highest ID if multiple)
    const { data: assigns } = await supabase
      .from('athlete_plan_assignments')
      .select('plan_id, plans(id, month, year)')
      .eq('athlete_id', id);
    if (!assigns?.length) { setPlanWorkouts({}); return; }
    const monthMatches = assigns.filter(a =>
      a.plans?.month === logMonth + 1 && a.plans?.year === logYear
    );
    if (!monthMatches.length) { setPlanWorkouts({}); setCurrentPlanId(null); return; }
    // Use only the single highest-ID plan (most recently created) to avoid merged duplicates
    const best = monthMatches.reduce((b, a) => !b || a.plans.id > b.plans.id ? a : b, null);
    setCurrentPlanId(best.plans.id);
    const { data: workouts } = await supabase
      .from('plan_workouts')
      .select('workout_date, custom_description, athlete_rest, swapped_from_date')
      .eq('plan_id', best.plans.id)
      .gte('workout_date', start)
      .lte('workout_date', end);
    const map = {};
    (workouts ?? []).forEach(w => {
      map[w.workout_date] = {
        text: w.custom_description ?? '',
        athleteRest: w.athlete_rest ?? false,
        swappedFromDate: w.swapped_from_date ?? null,
      };
    });
    setPlanWorkouts(map);
  }

  async function fetchMonthFlags() {
    const monthStr = String(logMonth + 1).padStart(2, '0');
    const start = `${logYear}-${monthStr}-01`;
    const end = `${logYear}-${monthStr}-${String(getDaysInMonth(logYear, logMonth)).padStart(2, '0')}`;
    // Flags that started on or before month end AND (unresolved OR resolved after month start)
    const { data } = await supabase
      .from('health_flags')
      .select('id, flag_date, type, body_part, illness_type, pain_scale, notes, resolved, resolved_at')
      .eq('athlete_id', id)
      .lte('flag_date', end)
      .or(`resolved.eq.false,resolved_at.gte.${start}`);
    setMonthFlags(data ?? []);
  }

  async function markFlagResolved(flagId) {
    await supabase
      .from('health_flags')
      .update({ resolved: true, resolved_at: new Date().toISOString() })
      .eq('id', flagId);
    await fetchMonthFlags();
  }

  function buildFlagDateMap(flags) {
    const todayStr = new Date().toISOString().slice(0, 10);
    const monthStr = String(logMonth + 1).padStart(2, '0');
    const monthStart = `${logYear}-${monthStr}-01`;
    const monthEnd = `${logYear}-${monthStr}-${String(getDaysInMonth(logYear, logMonth)).padStart(2, '0')}`;
    const map = {}; // { 'YYYY-MM-DD': flag[] }
    for (const flag of flags) {
      const rangeStart = flag.flag_date > monthStart ? flag.flag_date : monthStart;
      const rawEnd = flag.resolved ? (flag.resolved_at?.slice(0, 10) ?? todayStr) : todayStr;
      const rangeEnd = rawEnd > monthEnd ? monthEnd : rawEnd;
      let d = new Date(rangeStart + 'T00:00:00');
      const endDate = new Date(rangeEnd + 'T00:00:00');
      while (d <= endDate) {
        const ds = d.toISOString().slice(0, 10);
        if (!map[ds]) map[ds] = [];
        map[ds].push(flag);
        d.setDate(d.getDate() + 1);
      }
    }
    return map;
  }

  function handleCoachLogSaved() {
    fetchMonthLogs();
  }

  async function handleCoachRestDay(date, isRest) {
    if (!currentPlanId) return 'No plan assigned for this month — create a plan first to log a rest day.';
    const existing = planWorkouts[date];
    let error;
    if (existing) {
      ({ error } = await supabase.from('plan_workouts').update({ athlete_rest: isRest })
        .eq('plan_id', currentPlanId).eq('workout_date', date));
    } else {
      ({ error } = await supabase.from('plan_workouts').insert({
        plan_id: currentPlanId, workout_date: date, athlete_rest: isRest, custom_description: null,
      }));
    }
    if (error) return error.message;
    setPlanWorkouts(prev => ({
      ...prev,
      [date]: { ...(prev[date] ?? { text: '', swappedFromDate: null }), athleteRest: isRest },
    }));
  }

  async function handleDeleteLog(date, session = null) {
    let query = supabase.from('training_logs').delete().eq('athlete_id', id).eq('log_date', date);
    if (session) query = query.eq('session', session);
    const { error } = await query;
    if (error) { alert('Failed to clear log: ' + error.message); return; }
    // Also clear any rest day flag on the plan_workout row for this date
    if (currentPlanId && planWorkouts[date]?.athleteRest) {
      await supabase.from('plan_workouts').update({ athlete_rest: false })
        .eq('plan_id', currentPlanId).eq('workout_date', date);
      setPlanWorkouts(prev => ({
        ...prev,
        [date]: { ...prev[date], athleteRest: false },
      }));
    }
    // Refresh from DB to ensure consistent state
    await fetchMonthLogs();
    setLogViewDay(null);
  }

  const initials = athlete?.full_name
    ? athlete.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Link to="/coach/athletes" className="back-link">
        ← All Athletes
      </Link>

      {/* Profile Header */}
      <div className="profile-header">
        <label style={{ cursor: 'pointer' }} title="Upload athlete photo">
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoachAvatarUpload} disabled={avatarUploading} />
          <div className="profile-avatar">
            {athlete?.avatar_url
              ? <img src={athlete.avatar_url} alt={athlete?.full_name} />
              : initials
            }
            <div className="profile-avatar-overlay">{avatarUploading ? '…' : 'Upload'}</div>
          </div>
        </label>
        <div className="profile-meta">
          <h2>{athlete?.full_name}</h2>
          <p>
            {athlete?.email}
            {athlete?.grade_level && ` · ${formatGradeLevel(athlete.grade_level)}`}
            {athlete?.events?.length > 0 && ` · ${athlete.events.map(e => EVENTS[e]?.label ?? e).join(', ')}`}
          </p>
        </div>
        {vdot && <span className="vdot-badge" style={{ marginLeft: 'auto' }}>VDOT <span>{vdot}</span></span>}
      </div>

      {/* Training Log Calendar */}
      {(() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const monthName = new Date(logYear, logMonth, 1).toLocaleString('en-US', { month: 'long' });
        const weeks = buildCalendarCells(logYear, logMonth);
        const flagDateMap = buildFlagDateMap(monthFlags);
        const monthStr = String(logMonth + 1).padStart(2, '0');
        const racesByDate = {};
        (races ?? []).forEach(r => {
          if (r.race_date?.startsWith(`${logYear}-${monthStr}`)) racesByDate[r.race_date] = r;
        });
        return (
          <div className="section-card" style={{ marginTop: 24, marginBottom: 24 }}>
            <div className="section-card-header">
              <span className="section-card-title">Training Log</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  className="btn-xs btn-xs-ghost"
                  onClick={() => navigate(`/coach/plan-builder?athlete=${id}`)}
                  title="Go to this athlete's plan builder"
                >
                  Plan Builder →
                </button>
                {Object.keys(planWorkouts).length > 0 && (
                  <button
                    className="btn-xs btn-xs-ghost"
                    onClick={downloadMonthlyPlanPDF}
                    disabled={pdfGenerating}
                    title="Download this month's training plan as a PDF"
                    style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    {pdfGenerating ? 'Generating…' : '↓ Monthly Plan PDF'}
                  </button>
                )}
                <div className="tl-cal-month-nav">
                  <button className="tl-cal-nav-btn" onClick={goToPrevMonth}>←</button>
                  <span className="tl-cal-month-label">{monthName} {logYear}</span>
                  <button className="tl-cal-nav-btn" onClick={goToNextMonth}>→</button>
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--gray-100)' }}>
              <div className="calendar-grid">
                {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                  <div key={d} className="calendar-header-cell">{d}</div>
                ))}
              </div>
              {weeks.map((week, wi) => {
                const weekMiles = week.reduce((sum, dateStr) => {
                  if (!dateStr) return sum;
                  return sum + (monthLogs[dateStr]?.total_miles ?? 0);
                }, 0);
                const weekRunMiles = week.reduce((sum, dateStr) => {
                  if (!dateStr) return sum;
                  const log = monthLogs[dateStr];
                  return sum + (log?.running_miles ?? log?.total_miles ?? 0);
                }, 0);
                const weekXtMiles = week.reduce((sum, dateStr) => {
                  if (!dateStr) return sum;
                  return sum + (monthLogs[dateStr]?.xt_miles ?? 0);
                }, 0);
                return (
                  <div key={wi} className="calendar-grid calendar-week-row">
                    {week.map((dateStr, i) => {
                      if (!dateStr) return <div key={i} className="day-cell day-cell--outside" />;
                      const log = monthLogs[dateStr];
                      const planned = planWorkouts[dateStr];
                      const flags = flagDateMap[dateStr] ?? [];
                      const race = racesByDate[dateStr];
                      const isToday = dateStr === todayStr;
                      const dayNum = parseInt(dateStr.slice(8), 10);
                      const dayVacs = vacations.filter(v => dateStr >= v.start_date && dateStr <= v.end_date);
                      const isAmPmDay = planned?.text && /\bam\s*:/i.test(planned.text) && /\bpm\s*:/i.test(planned.text);
                      const amDone = !!monthLogSessions[dateStr]?.am;
                      const pmDone = !!monthLogSessions[dateStr]?.pm;
                      const logState = log
                        ? (isAmPmDay ? (amDone && pmDone ? 'full' : 'partial') : 'full')
                        : null;
                      return (
                        <div
                          key={dateStr}
                          className={`day-cell${isToday ? ' day-cell--today' : ''}${race ? ' day-cell--race' : ''}${logState === 'full' ? ' day-cell--logged' : ''}${logState === 'partial' ? ' day-cell--partial' : ''}${flags.length ? ' day-cell--flagged' : ''}`}
                          onClick={() => setLogViewDay({ date: dateStr, log, planned, flags })}
                        >
                          <div className="day-number">{dayNum}</div>
                          {race && (
                            <div className={`day-race-badge priority-${race.priority}`}>
                              {race.priority} — {race.name}
                              {(() => {
                                const evts = parseRaceEvents(race.event);
                                if (evts.length > 1) {
                                  return evts.map((ek, ei) => {
                                    const t = savedResults[`${race.id}-${ek}`] ?? (ei === 0 && race.result_time_seconds ? formatTime(race.result_time_seconds) : null);
                                    return t ? <span key={ek} className="day-race-result">{(EVENTS[ek]?.label ?? ek)}: {t}</span> : null;
                                  });
                                }
                                return race.result_time_seconds > 0 ? <span className="day-race-result">{formatTime(race.result_time_seconds)}</span> : null;
                              })()}
                            </div>
                          )}
                          {log ? (
                            <>
                              {logState === 'partial' && (
                                <div className="day-logged-badge day-logged-badge--partial" style={{ fontSize: 8, marginBottom: 2 }}>
                                  ½ {amDone ? 'AM' : 'PM'} logged
                                </div>
                              )}
                              <div className="day-preview">
                                {(log.segments ?? []).map(s => formatSegmentActual(s)).filter(Boolean).join(' · ') || 'Workout'}
                              </div>
                              {log.total_miles > 0 && (
                                (log.running_miles || 0) > 0.001 && (log.xt_miles || 0) > 0.001 ? (
                                  <div className="tl-cal-miles">
                                    <span>Run {log.running_miles.toFixed(1)}</span>
                                    <span style={{ color: 'var(--gray-400)' }}>XT {log.xt_miles.toFixed(1)}</span>
                                  </div>
                                ) : (log.xt_miles || 0) > 0.001 && (log.running_miles || 0) <= 0.001 ? (
                                  <div className="tl-cal-miles">
                                    <span style={{ color: 'var(--gray-400)' }}>XT {log.xt_miles.toFixed(1)} mi</span>
                                  </div>
                                ) : (
                                  <div className="tl-cal-miles">{log.total_miles.toFixed(1)} mi</div>
                                )
                              )}
                            </>
                          ) : planned?.text ? (
                            <>
                              {planned.swappedFromDate && (
                                <div className="day-swapped-badge" title={`Moved from ${planned.swappedFromDate}`}>↔ Moved</div>
                              )}
                              {planned.athleteRest && (
                                <div className="day-rest-badge">Rest Day</div>
                              )}
                              <div className="day-preview day-preview--planned">{renderWorkoutText(resolvePaceTags(planned.text, applyAltitudeAdjustment(paces, altitudeFt)))}</div>
                            </>
                          ) : planned?.athleteRest ? (
                            <div className="day-rest-badge">Rest Day</div>
                          ) : (
                            <div className="day-preview day-preview--rest">Rest</div>
                          )}
                          {flags.map((f, fi) => (
                            <div key={fi} className="tl-cal-flag-chip">
                              {f.type === 'injury' ? `🩹 ${f.body_part ?? 'Injury'}` : `🤒 ${f.illness_type ?? 'Illness'}`}
                            </div>
                          ))}
                          {dayVacs.map((v, vi) => (
                            <div key={vi} style={{ fontSize: 10, fontWeight: 600, color: '#92400e', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 3, padding: '1px 4px', marginTop: 2 }}>
                              ✈ {v.title || 'Trip'}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    <div className="week-volume-row-total">
                      Week: <strong>
                        {weekMiles > 0.005
                          ? weekRunMiles > 0.005 && weekXtMiles > 0.005
                            ? `${weekRunMiles.toFixed(1)} run · ${weekXtMiles.toFixed(1)} XT · ${weekMiles.toFixed(1)} mi`
                            : weekXtMiles > 0.005 && weekRunMiles <= 0.005
                              ? `${weekMiles.toFixed(1)} mi (XT)`
                              : weekMiles.toFixed(1) + ' mi'
                          : '0 mi'}
                      </strong>
                    </div>
                  </div>
                );
              })}
              {(() => {
                const monthTotal = Object.values(monthLogs).reduce((sum, log) => sum + (log.total_miles ?? 0), 0);
                return (
                  <div className="month-volume-total">
                    Month total: <strong>{monthTotal > 0.005 ? monthTotal.toFixed(1) + ' mi' : '0 mi'}</strong>
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* Goals — editable by coach. Athletes still manage from
          their own Profile tab; this surface lets the coach add
          or revise based on training context (state meet target,
          OTQ, etc.). */}
      <div className="section-card" style={{ marginBottom: 16 }}>
        <div className="section-card-header">
          <span className="section-card-title">Goals</span>
          <button
            className="btn-xs btn-xs-ghost"
            onClick={() => {
              setGoalDraft({ event: null, goal_time_str: '', notes: '' });
              setGoalErr('');
            }}
          >
            + Add goal
          </button>
        </div>
        <div style={{ padding: '12px 24px 16px' }}>
          {goals.length === 0 && !goalDraft && (
            <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
              No active goals yet. Add one to give the athlete a target.
            </div>
          )}
          {goals.map((g, i) => (
            <div
              key={g.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                padding: '10px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--gray-100)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>
                  {EVENTS[g.event]?.label ?? g.event}
                </div>
                {g.notes && (
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                    {g.notes}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontFamily: 'Menlo, monospace',
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--navy)',
                }}
              >
                {formatTime(g.goal_seconds)}
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button
                  className="btn-xs btn-xs-ghost"
                  style={{ fontSize: 11 }}
                  onClick={() => {
                    setGoalDraft({
                      id: g.id,
                      event: g.event,
                      goal_time_str: formatTime(g.goal_seconds),
                      notes: g.notes ?? '',
                    });
                    setGoalErr('');
                  }}
                >
                  Edit
                </button>
                <button
                  className="btn-xs btn-xs-ghost"
                  style={{ fontSize: 11, color: '#dc2626' }}
                  onClick={() => deleteGoal(g.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {goalDraft && (
        <CoachGoalEditModal
          draft={goalDraft}
          existingEvents={goals.filter(g => g.id !== goalDraft.id).map(g => g.event)}
          err={goalErr}
          saving={savingGoal}
          onChange={patch => setGoalDraft(prev => ({ ...prev, ...patch }))}
          onCancel={() => { setGoalDraft(null); setGoalErr(''); }}
          onSave={saveGoalDraft}
        />
      )}

      {/* Habits — daily check-ins. Coach + athlete share the
          definitions; the athlete checks them off from their iOS
          dashboard each day, and completions surface on each day's
          log modal in green / red. */}
      <div className="section-card" style={{ marginBottom: 16 }}>
        <div className="section-card-header">
          <span className="section-card-title">Habits</span>
          <button
            className="btn-xs btn-xs-ghost"
            onClick={() => {
              setHabitDraft({ name: '', days_of_week: [1, 2, 3, 4, 5], notes: '' });
              setHabitErr('');
            }}
          >
            + Add habit
          </button>
        </div>
        <div style={{ padding: '12px 24px 16px' }}>
          {habits.length === 0 && !habitDraft && (
            <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
              No habits yet. Add one to give the athlete a daily check-in.
            </div>
          )}
          {habits.map((h, i) => (
            <div
              key={h.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                padding: '10px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--gray-100)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>
                  {h.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                  {describeDays(h.days_of_week)}
                  {h.notes ? ` · ${h.notes}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button
                  className="btn-xs btn-xs-ghost"
                  style={{ fontSize: 11 }}
                  onClick={() => {
                    setHabitDraft({
                      id: h.id,
                      name: h.name,
                      days_of_week: [...(h.days_of_week ?? [])],
                      notes: h.notes ?? '',
                    });
                    setHabitErr('');
                  }}
                >
                  Edit
                </button>
                <button
                  className="btn-xs btn-xs-ghost"
                  style={{ fontSize: 11, color: '#dc2626' }}
                  onClick={() => deleteHabit(h.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {habitDraft && (
        <CoachHabitEditModal
          draft={habitDraft}
          err={habitErr}
          saving={savingHabit}
          onChange={patch => setHabitDraft(prev => ({ ...prev, ...patch }))}
          onCancel={() => { setHabitDraft(null); setHabitErr(''); }}
          onSave={saveHabitDraft}
        />
      )}

      <div className="profile-grid">
        {/* Left: Benchmark Entry */}
        <div>
          <div className="section-card">
            <div className="section-card-header">
              <span className="section-card-title">Add Benchmarks</span>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <form onSubmit={handleAddBenchmarks}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                  {benchmarkRows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <select
                        className="form-input form-select"
                        style={{ padding: '6px 8px', fontSize: 12, width: '100%' }}
                        value={row.event}
                        onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, event: e.target.value } : r))}
                      >
                        {Object.entries(EVENTS)
                          // Relays are race types, not benchmark events.
                          // Splits ('800m-split') stay so coaches can
                          // record an athlete's flying-start splits.
                          .filter(([key]) => !isRelayEvent(key))
                          .map(([key, ev]) => (
                          <option key={key} value={key}>{ev.label}</option>
                        ))}
                      </select>
                      {row.event === 'other' && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                          <input
                            className="form-input"
                            style={{ padding: '6px 8px', fontSize: 12, flex: 1, minWidth: 0 }}
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="Distance"
                            value={row.customDist}
                            onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, customDist: e.target.value } : r))}
                          />
                          <select
                            className="form-input form-select"
                            style={{ padding: '6px 8px', fontSize: 12, flex: '0 0 60px' }}
                            value={row.customUnit}
                            onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, customUnit: e.target.value } : r))}
                          >
                            <option value="mi">mi</option>
                            <option value="km">km</option>
                          </select>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          className="form-input"
                          style={{ padding: '6px 8px', fontSize: 12, flex: '0 0 80px' }}
                          type="text"
                          placeholder="4:32.56"
                          value={row.time}
                          onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, time: e.target.value } : r))}
                        />
                        <input
                          className="form-input"
                          style={{ padding: '6px 8px', fontSize: 12, flex: 1, minWidth: 0 }}
                          type="date"
                          value={row.date}
                          onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, date: e.target.value } : r))}
                        />
                        <button
                          type="button"
                          style={{ background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}
                          onClick={() => setBenchmarkRows(rows => rows.length > 1 ? rows.filter((_, j) => j !== i) : rows)}
                          title="Remove row"
                        >×</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="btn-xs btn-xs-ghost"
                    onClick={() => setBenchmarkRows(rows => [...rows, { event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }])}
                  >
                    + Add Row
                  </button>
                  {error && <span className="form-error" style={{ margin: 0 }}>{error}</span>}
                  <button className="btn btn-primary" type="submit" disabled={saving} style={{ marginLeft: 'auto', width: 'auto', padding: '8px 20px' }}>
                    {saving ? 'Saving...' : `Save ${benchmarkRows.filter(r => r.time.trim()).length || ''} Benchmark${benchmarkRows.filter(r => r.time.trim()).length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Benchmark History */}
          {benchmarks.length > 0 && (
            <div className="section-card" style={{ marginTop: 16 }}>
              <div className="section-card-header">
                <span className="section-card-title">Benchmark History</span>
              </div>
              <div style={{ padding: '12px 16px' }}>
                {pinnedBenchmarkId && (
                  <div className="bm-override-banner">
                    Override active — paces based on pinned benchmark, not highest VDOT.{' '}
                    <button className="bm-override-reset-link" onClick={handleClearPin}>Reset to best</button>
                  </div>
                )}
                <div className="benchmark-list" style={{ marginTop: 0 }}>
                  {benchmarks.map((b) => {
                    const bResult = calculatePaces(b.event, b.time_seconds);
                    const bVdot = bResult?.vdot ?? null;
                    const isActive = b.id === (pinnedBenchmarkId ?? bestBenchmarkId);
                    const isBest = b.id === bestBenchmarkId;
                    const isPinned = b.id === pinnedBenchmarkId;
                    return (
                      <div key={b.id} className={`benchmark-item${isActive ? ' benchmark-item--active' : ''}`}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span className="benchmark-item-event">
                              {EVENTS[b.event]?.label ?? b.event}
                            </span>
                            {isActive && !isPinned && (
                              <span className="badge badge-gold" style={{ fontSize: 10 }}>Best VDOT</span>
                            )}
                            {isPinned && (
                              <span className="badge badge-blue" style={{ fontSize: 10 }}>Override</span>
                            )}
                          </div>
                          {b.race_date && (
                            <div className="benchmark-item-date">{b.race_date}</div>
                          )}
                          {b.notes && (
                            <div className="benchmark-item-date" style={{ fontStyle: 'italic' }}>{b.notes}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {bVdot && (
                            <span className="bm-vdot-chip" title="VDOT score">
                              {bVdot}
                            </span>
                          )}
                          <span className="benchmark-item-time">{formatTime(b.time_seconds)}</span>
                          {!isActive && !isPinned && !(isBest && pinnedBenchmarkId) && (
                            <button
                              className="btn-xs btn-xs-ghost"
                              style={{ fontSize: 11, padding: '2px 7px' }}
                              onClick={() => handlePinBenchmark(b.id)}
                              title="Use this benchmark for training paces"
                            >
                              Use
                            </button>
                          )}
                          {isBest && pinnedBenchmarkId && (
                            <button
                              className="btn-xs btn-xs-ghost"
                              style={{ fontSize: 11, padding: '2px 7px' }}
                              onClick={handleClearPin}
                              title="Reset to highest VDOT"
                            >
                              Reset
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteBenchmark(b.id)}
                            style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                            title="Delete"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Calculated Paces */}
        <div>
          <div className="section-card">
            <div className="section-card-header">
              <span className="section-card-title">Training Paces</span>
              {(() => {
                const activeBm = benchmarks.find(b => b.id === (pinnedBenchmarkId ?? bestBenchmarkId));
                if (!activeBm) return null;
                return (
                  <span className="cell-muted" style={{ fontSize: 12 }}>
                    {pinnedBenchmarkId ? '⚡ Override: ' : '★ Best: '}
                    {EVENTS[activeBm.event]?.label} — {formatTime(activeBm.time_seconds)}
                  </span>
                );
              })()}
            </div>

            {/* Altitude input */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>Training Altitude</span>
              <input
                className="form-input"
                type="number"
                min="0"
                max="15000"
                step="100"
                placeholder="e.g. 5280"
                value={altitudeInput}
                onChange={e => setAltitudeInput(e.target.value)}
                style={{ width: 90, padding: '4px 8px', fontSize: 12 }}
              />
              <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>ft</span>
              <button
                className="btn-xs btn-xs-ghost"
                onClick={handleSaveAltitude}
                disabled={altitudeSaving}
              >
                {altitudeSaving ? 'Saving…' : 'Save'}
              </button>
              {altitudeFt && altitudeFt >= 3000 && (
                <span style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 600 }}>
                  Altitude-adjusted ({altitudeFt.toLocaleString()} ft)
                </span>
              )}
            </div>
            {paces ? (() => {
              const displayPaces = applyAltitudeAdjustment(paces, altitudeFt);
              return (
              <table className="pace-table">
                <thead>
                  <tr>
                    <th>Zone</th>
                    <th>Per Mile</th>
                    <th>Per KM</th>
                    <th>Range (mi)</th>
                  </tr>
                </thead>
                <tbody>
                  {ZONE_ORDER.map(key => {
                    const z = displayPaces[key];
                    if (!z) return null;
                    return (
                      <tr key={key}>
                        <td className="zone-name">{z.label}</td>
                        <td className="pace-value">{z.perMile}</td>
                        <td className="pace-value">{z.perKm}</td>
                        <td className="pace-range">{z.rangeMile}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              );
            })() : (
              <div className="section-card-body">
                Add a benchmark to generate training paces.
              </div>
            )}
          </div>

          <div className="section-card" style={{ marginTop: 16 }}>
            <div className="section-card-header">
              <span className="section-card-title">Upcoming Races</span>
              <button
                className="btn-xs btn-xs-ghost"
                onClick={() => { if (showRaceForm) resetRaceForm(); else setShowRaceForm(true); }}
              >
                {showRaceForm ? 'Cancel' : '+ Add Race'}
              </button>
            </div>

            {showRaceForm && (
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{editRaceId ? 'Edit Race' : 'Add Race'}</div>
                <form onSubmit={handleAddRace}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Race Name</label>
                      <input className="form-input" type="text" value={raceName} onChange={e => setRaceName(e.target.value)} placeholder="State Championships" required />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Start Date</label>
                      <input className="form-input" type="date" value={raceFormDate} onChange={e => setRaceFormDate(e.target.value)} required />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                      <label className="form-label">End Date (optional — for multi-day meets)</label>
                      <input
                        className="form-input"
                        type="date"
                        value={raceFormDateEnd}
                        min={raceFormDate || undefined}
                        onChange={e => setRaceFormDateEnd(e.target.value)}
                        style={{ maxWidth: 240 }}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                      <label className="form-label">Events (optional — select one or more)</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, maxHeight: 140, overflowY: 'auto', padding: 8, border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                        {Object.entries(EVENTS)
                          .filter(([key]) => !key.endsWith('-split'))
                          .map(([key, ev]) => (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer', padding: '2px 0' }}>
                            <input
                              type="checkbox"
                              checked={raceEvents.includes(key)}
                              onChange={e => toggleRaceEventCheckbox(key, e.target.checked)}
                            />
                            {ev.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    {raceEvents.filter(isRelayEvent).map(relayKey => {
                      const legs = getRelayLegOptions(relayKey) ?? [];
                      if (legs.length <= 1) return null;
                      return (
                        <div key={relayKey} className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                          <label className="form-label">
                            Athlete's Leg in {EVENTS[relayKey]?.label ?? relayKey}
                          </label>
                          <select
                            className="form-input form-select"
                            value={relayLegs[relayKey] ?? ''}
                            onChange={e => setRelayLegs(prev => ({ ...prev, [relayKey]: e.target.value }))}
                            style={{ maxWidth: 240 }}
                            required
                          >
                            <option value="">— Select leg —</option>
                            {legs.map(m => (
                              <option key={m} value={String(m)}>{m}m</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                    {raceFormDateEnd && raceFormDateEnd !== raceFormDate && raceEvents.length > 0 && (
                      <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                        <label className="form-label">Event Days (multi-day meet)</label>
                        <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 8px' }}>
                          Pick which day each event runs. Defaults to the start date.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                          {raceEvents.map(ek => (
                            <div key={ek} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>
                                {EVENTS[ek]?.label ?? ek}
                              </span>
                              <input
                                type="date"
                                className="form-input"
                                min={raceFormDate || undefined}
                                max={raceFormDateEnd || undefined}
                                value={eventDates[ek] ?? raceFormDate ?? ''}
                                onChange={e => setEventDates(prev => ({ ...prev, [ek]: e.target.value }))}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Priority</label>
                      <select className="form-input form-select" value={racePriority} onChange={e => setRacePriority(e.target.value)}>
                        <option value="A">A Race — Peak goal</option>
                        <option value="B">B Race — Strong effort</option>
                        <option value="C">C Race — Training race</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Season</label>
                      <select
                        className="form-input form-select"
                        value={raceSeason}
                        onChange={e => setRaceSeason(e.target.value)}
                      >
                        <option value="">Auto (from date)</option>
                        {SEASON_OPTIONS.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Location (optional)</label>
                      <input className="form-input" type="text" value={raceLocation} onChange={e => setRaceLocation(e.target.value)} placeholder="City, State" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                      <label className="form-label">Notes (optional)</label>
                      <input className="form-input" type="text" value={raceNotes} onChange={e => setRaceNotes(e.target.value)} placeholder="Goals, course info..." />
                    </div>
                  </div>
                  {raceError && <p className="form-error" style={{ marginTop: 8 }}>{raceError}</p>}
                  <button className="btn btn-primary" type="submit" disabled={raceSaving} style={{ width: 'auto', padding: '8px 20px', marginTop: 12 }}>
                    {raceSaving ? 'Saving...' : (editRaceId ? 'Update Race' : 'Save Race')}
                  </button>
                </form>
              </div>
            )}

            {races.length === 0 && !showRaceForm ? (
              <div className="section-card-body" style={{ color: 'var(--gray-400)', fontSize: 13 }}>
                No races. Click "+ Add Race" to add one.
              </div>
            ) : (() => {
              const today = new Date(); today.setHours(0, 0, 0, 0);
              // Multi-day races partition by their LAST day — a 3-day
              // meet that started yesterday and ends tomorrow still
              // belongs in Upcoming.
              const effectiveEnd = r => new Date((r.race_date_end ?? r.race_date) + 'T00:00:00');
              const upcoming = races.filter(r => effectiveEnd(r) >= today);
              const past = races.filter(r => effectiveEnd(r) < today);
              return (
                <>
                  {upcoming.length > 0 && (
                    <div className="race-list">
                      {upcoming.map(race => {
                        const days = daysUntil(race.race_date);
                        return (
                          <div key={race.id} className="race-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                              <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                              <div className="race-item-main">
                                <div className="race-item-name">{race.name}</div>
                                <div className="race-item-meta">
                                  {getEventLabels(race.event) || null}
                                  {race.location && ` · ${race.location}`}
                                </div>
                              </div>
                              <div className="race-item-right">
                                <div className="race-item-date">{formatRaceDateRange(race.race_date, race.race_date_end)}</div>
                                <div className={`race-countdown${days <= 7 ? ' race-countdown--soon' : ''}`}>
                                  {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`}
                                </div>
                              </div>
                              <button
                                className="btn-xs btn-xs-ghost"
                                style={{ fontSize: 11, flexShrink: 0 }}
                                onClick={() => {
                                  setRaceName(race.name); setRaceFormDate(race.race_date);
                                  setRaceFormDateEnd(race.race_date_end ?? '');
                                  const evts = parseRaceEvents(race.event);
                                  setRaceEvents(evts);
                                  const legsInit = { ...(race.relay_legs ?? {}) };
                                  if (Object.keys(legsInit).length === 0 && race.relay_leg) {
                                    const firstRelay = evts.find(isRelayEvent);
                                    if (firstRelay) legsInit[firstRelay] = race.relay_leg;
                                  }
                                  setRelayLegs(legsInit);
                                  setEventDates(race.event_dates ?? {});
                                  setRaceSeason(race.season ?? '');
                                  setRaceLocation(race.location ?? ''); setRacePriority(race.priority);
                                  setRaceNotes(race.notes ?? ''); setEditRaceId(race.id); setShowRaceForm(true);
                                }}
                                title="Edit race"
                              >Edit</button>
                              <button
                                onClick={() => handleDeleteRace(race.id)}
                                style={{ background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
                                title="Delete"
                              >×</button>
                            </div>
                            {renderResultInputs(race)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {past.length > 0 && (
                    <>
                      <div style={{ padding: '8px 24px 4px', fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: upcoming.length > 0 ? '1px solid var(--gray-100)' : 'none' }}>
                        Past Races
                      </div>
                      <div className="race-list">
                        {[...past].reverse().map(race => (
                          <div key={race.id} className="race-item race-item--past" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                              <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                              <div className="race-item-main">
                                <div className="race-item-name">{race.name}</div>
                                <div className="race-item-meta">
                                  {getEventLabels(race.event) || null}
                                  {race.location && ` · ${race.location}`}
                                </div>
                              </div>
                              <div className="race-item-right">
                                <div className="race-item-date">{formatRaceDateRange(race.race_date, race.race_date_end)}</div>
                              </div>
                              <button
                                className="btn-xs btn-xs-ghost"
                                style={{ fontSize: 11, flexShrink: 0 }}
                                onClick={() => {
                                  setRaceName(race.name); setRaceFormDate(race.race_date);
                                  setRaceFormDateEnd(race.race_date_end ?? '');
                                  const evts = parseRaceEvents(race.event);
                                  setRaceEvents(evts);
                                  const legsInit = { ...(race.relay_legs ?? {}) };
                                  if (Object.keys(legsInit).length === 0 && race.relay_leg) {
                                    const firstRelay = evts.find(isRelayEvent);
                                    if (firstRelay) legsInit[firstRelay] = race.relay_leg;
                                  }
                                  setRelayLegs(legsInit);
                                  setEventDates(race.event_dates ?? {});
                                  setRaceSeason(race.season ?? '');
                                  setRaceLocation(race.location ?? ''); setRacePriority(race.priority);
                                  setRaceNotes(race.notes ?? ''); setEditRaceId(race.id); setShowRaceForm(true);
                                }}
                                title="Edit race"
                              >Edit</button>
                              <button
                                onClick={() => handleDeleteRace(race.id)}
                                style={{ background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
                                title="Delete"
                              >×</button>
                            </div>
                            {renderResultInputs(race)}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>

          {/* Improvement Tracking */}
          {(() => {
            // Season key: explicit `benchmark.season` tag wins; falls
            // back to date-based inference for legacy rows. Lives in
            // src/lib/seasons.js.
            const getSeasonKey = b => seasonKeyForBenchmark(b);

            function isBeforeApex(dateStr) {
              // Prefer the exact joined_apex_date; fall back to legacy
              // month/year (first of month) when the migration hasn't
              // backfilled yet.
              const joined = athlete?.joined_apex_date
                ?? (athlete?.billing_start_year && athlete?.billing_start_month
                  ? `${athlete.billing_start_year}-${String(athlete.billing_start_month).padStart(2, '0')}-01`
                  : null);
              if (!joined || !dateStr) return false;
              return dateStr < joined; // ISO YYYY-MM-DD strings sort lexicographically
            }

            const allSeasonMap = new Map();
            const byEvent = {};
            for (const b of benchmarks) {
              // Skip entries with no notes (un-tagged manual entries)
              if (!b.notes) continue;
              const isBaseline = b.notes === 'Baseline entry';
              let ev = b.event;
              if (ev && ev.startsWith('[')) { try { ev = JSON.parse(ev)[0] ?? ev; } catch {} }
              // Defensive: drop any relay-race-keyed benchmarks that
              // slipped in pre-split-mapping. Splits ('800m-split')
              // are the right rep for a relay leg in this table.
              if (isRelayEvent(ev)) continue;
              if (!byEvent[ev]) byEvent[ev] = { baseline: null, seasons: {} };
              if (isBaseline || isBeforeApex(b.race_date)) {
                // Baseline entries always count as pre-apex, regardless of date
                if (!byEvent[ev].baseline || b.time_seconds < byEvent[ev].baseline.time_seconds)
                  byEvent[ev].baseline = b;
              } else {
                if (!b.race_date) continue;
                const sk = getSeasonKey(b);
                if (!sk) continue;
                allSeasonMap.set(sk.label, sk.sort);
                if (!byEvent[ev].seasons[sk.label] || b.time_seconds < byEvent[ev].seasons[sk.label])
                  byEvent[ev].seasons[sk.label] = b.time_seconds;
              }
            }

            const sortedSeasons = [...allSeasonMap.entries()]
              .sort((a, b) => a[1] - b[1])
              .map(([label]) => label);

            const eventEntries = Object.entries(byEvent)
              .filter(([, d]) => d.baseline !== null || Object.keys(d.seasons).length > 0);

            if (eventEntries.length === 0) return null;

            return (
              <div className="section-card" style={{ marginTop: 16 }}>
                <div className="section-card-header">
                  <span className="section-card-title">Improvement Tracking</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Before Apex</th>
                        {sortedSeasons.map(s => <th key={s}>{s}</th>)}
                        <th>Improvement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventEntries.map(([event, data]) => {
                        const eventSeasons = sortedSeasons.filter(s => data.seasons[s]);
                        const latestSeason = eventSeasons.at(-1);
                        const firstSeason = eventSeasons[0];
                        const latestTime = latestSeason ? data.seasons[latestSeason] : null;
                        const refTime = data.baseline
                          ? data.baseline.time_seconds
                          : (eventSeasons.length >= 2 ? data.seasons[firstSeason] : null);
                        const improvement = (refTime !== null && latestTime !== null) ? refTime - latestTime : null;
                        return (
                          <tr key={event}>
                            <td style={{ fontWeight: 700, color: 'var(--navy)', whiteSpace: 'nowrap' }}>
                              {EVENTS[event]?.label ?? event}
                            </td>
                            <td style={{
                              fontFamily: 'monospace',
                              color: data.baseline ? 'var(--gray-500)' : 'var(--gray-300)',
                              fontStyle: data.baseline ? 'normal' : 'italic',
                            }}>
                              {data.baseline ? formatTime(data.baseline.time_seconds) : '—'}
                            </td>
                            {sortedSeasons.map(s => (
                              <td key={s} style={{
                                fontFamily: 'monospace',
                                fontWeight: data.seasons[s] ? 700 : 400,
                                color: data.seasons[s] ? 'var(--navy)' : 'var(--gray-200)',
                              }}>
                                {data.seasons[s] ? formatTime(data.seasons[s]) : '—'}
                              </td>
                            ))}
                            <td>
                              {improvement !== null ? (
                                improvement > 0 ? (
                                  <span style={{ background: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 12, borderRadius: 12, padding: '2px 10px' }}>
                                    -{fmtDelta(improvement)}
                                  </span>
                                ) : improvement < 0 ? (
                                  <span style={{ background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 12, borderRadius: 12, padding: '2px 10px' }}>
                                    +{fmtDelta(improvement)}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>±0</span>
                                )
                              ) : <span style={{ color: 'var(--gray-300)', fontSize: 13 }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Athlete Info */}
      {(isAdmin || athlete?.school_name || athlete?.gender || athlete?.birth_date || athlete?.phone || athlete?.address || athlete?.shirt_size || athlete?.grade_level) && (
        <div className="section-card" style={{ marginTop: 24 }}>
          <div className="section-card-header">
            <span className="section-card-title">Athlete Info</span>
            {isAdmin && !athleteInfoEditing && (
              <button className="btn-xs btn-xs-ghost" onClick={() => {
                setAthleteInfoForm({
                  school_name: athlete?.school_name ?? '',
                  grade_level: athlete?.grade_level ?? '',
                  gender: athlete?.gender ?? '',
                  birth_date: athlete?.birth_date ?? '',
                  phone: athlete?.phone ?? '',
                  shirt_size: athlete?.shirt_size ?? '',
                  address: athlete?.address ?? '',
                });
                setAthleteInfoEditing(true);
                setAthleteInfoSaved(false);
              }}>Edit</button>
            )}
            {athleteInfoSaved && <span style={{ fontSize: 12, color: 'var(--green, #15803d)', fontWeight: 600 }}>Saved ✓</span>}
          </div>

          {isAdmin && athleteInfoEditing ? (
            <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">School</label>
                  <input className="form-input" type="text" value={athleteInfoForm.school_name ?? ''}
                    onChange={e => setAthleteInfoForm(p => ({ ...p, school_name: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Level / Grade</label>
                  <select className="form-input form-select" value={athleteInfoForm.grade_level ?? ''}
                    onChange={e => setAthleteInfoForm(p => ({ ...p, grade_level: e.target.value }))}>
                    <option value="">— None —</option>
                    <optgroup label="Middle School">
                      {['6th','7th','8th'].map(g => <option key={g} value={g}>{g} Grade</option>)}
                    </optgroup>
                    <optgroup label="High School">
                      {['9th','10th','11th','12th'].map(g => <option key={g} value={g}>{g} Grade</option>)}
                    </optgroup>
                    <option value="collegiate">Collegiate</option>
                    <option value="post-collegiate">Post-Collegiate</option>
                    <option value="pro">★ Apex Elite Pro</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Gender</label>
                  <select className="form-input form-select" value={athleteInfoForm.gender ?? ''}
                    onChange={e => setAthleteInfoForm(p => ({ ...p, gender: e.target.value }))}>
                    <option value="">— None —</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Shirt Size</label>
                  <select className="form-input form-select" value={athleteInfoForm.shirt_size ?? ''}
                    onChange={e => setAthleteInfoForm(p => ({ ...p, shirt_size: e.target.value }))}>
                    <option value="">— None —</option>
                    {['XS','S','M','L','XL','XXL'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Date of Birth</label>
                  <input className="form-input" type="date" value={athleteInfoForm.birth_date ?? ''}
                    onChange={e => setAthleteInfoForm(p => ({ ...p, birth_date: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Phone</label>
                  <input className="form-input" type="text" value={athleteInfoForm.phone ?? ''}
                    onChange={e => setAthleteInfoForm(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0, gridColumn: 'span 2' }}>
                  <label className="form-label">Address</label>
                  <input className="form-input" type="text" value={athleteInfoForm.address ?? ''}
                    onChange={e => setAthleteInfoForm(p => ({ ...p, address: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ width: 'auto', padding: '7px 18px', fontSize: 13 }}
                  onClick={handleSaveAthleteInfo} disabled={athleteInfoSaving}>
                  {athleteInfoSaving ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-secondary" style={{ width: 'auto', padding: '7px 14px', fontSize: 13 }}
                  onClick={() => setAthleteInfoEditing(false)} disabled={athleteInfoSaving}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="athlete-info-grid">
              {athlete?.school_name && (
                <div className="athlete-info-item"><label>School</label><span>{athlete.school_name}</span></div>
              )}
              {athlete?.grade_level && (
                <div className="athlete-info-item"><label>Grade</label><span>{formatGradeLevel(athlete.grade_level)}</span></div>
              )}
              {athlete?.gender && (
                <div className="athlete-info-item"><label>Gender</label><span>{athlete.gender.charAt(0).toUpperCase() + athlete.gender.slice(1)}</span></div>
              )}
              {athlete?.birth_date && (
                <div className="athlete-info-item">
                  <label>Date of Birth</label>
                  <span>{new Date(athlete.birth_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              )}
              {athlete?.phone && (
                <div className="athlete-info-item"><label>Phone</label><span>{athlete.phone}</span></div>
              )}
              {athlete?.shirt_size && (
                <div className="athlete-info-item"><label>Shirt Size</label><span>{athlete.shirt_size}</span></div>
              )}
              {athlete?.address && (
                <div className="athlete-info-item" style={{ gridColumn: 'span 2' }}><label>Address</label><span>{athlete.address}</span></div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Parents */}
      <div className="section-card" style={{ marginTop: 24 }}>
        <div className="section-card-header">
          <span className="section-card-title">Parents / Guardians</span>
        </div>
        <div style={{ padding: '16px 24px' }}>
          <form onSubmit={handleAddParent} style={{ display: 'flex', gap: 8, marginBottom: parents.length > 0 ? 16 : 0 }}>
            <input
              className="form-input"
              type="email"
              placeholder="Parent's email address"
              value={parentEmail}
              onChange={e => setParentEmail(e.target.value)}
              required
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary" type="submit" disabled={parentSaving} style={{ width: 'auto', padding: '10px 20px' }}>
              {parentSaving ? 'Linking...' : 'Link Parent'}
            </button>
          </form>
          {parentError && <p className="form-error" style={{ marginTop: 8 }}>{parentError}</p>}
          {parents.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {parents.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{p.profiles?.full_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{p.profiles?.email}</div>
                  </div>
                  <button
                    onClick={() => handleRemoveParent(p.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
                    title="Remove"
                  >×</button>
                </div>
              ))}
            </div>
          )}
          {parents.length === 0 && !parentError && (
            <p style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 8 }}>No parents linked yet.</p>
          )}
        </div>
      </div>


      {/* Calendar & Events */}
      {(() => {
        const EQUIPMENT_OPTIONS = [
          { key: 'track', label: 'Track' },
          { key: 'treadmill', label: 'Treadmill' },
          { key: 'roads', label: 'Roads / Trails' },
          { key: 'weight_room', label: 'Weight Room' },
          { key: 'pool', label: 'Pool' },
          { key: 'gym', label: 'Gym' },
        ];

        function datesInRange(start, end) {
          if (!start || !end || end < start) return [];
          const dates = [];
          const d = new Date(start + 'T00:00:00');
          const last = new Date(end + 'T00:00:00');
          while (d <= last) { dates.push(d.toISOString().slice(0, 10)); d.setDate(d.getDate() + 1); }
          return dates;
        }

        function fmtVacDate(dateStr) {
          return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }

        const rangeForForm = datesInRange(vacStart, vacEnd);

        return (
          <div className="section-card" style={{ marginTop: 24 }}>
            <div className="section-card-header">
              <span className="section-card-title">Calendar & Events</span>
              <button className="btn-xs btn-xs-ghost" onClick={() => { setShowVacForm(v => !v); setVacError(''); }}>
                {showVacForm ? 'Cancel' : '+ Add Trip'}
              </button>
            </div>

            {showVacForm && (
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                <form onSubmit={handleAddVacation}>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Title</label>
                    <input className="form-input" type="text" value={vacTitle} onChange={e => setVacTitle(e.target.value)} placeholder="Beach Trip, Conference, Recovery Week..." />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Start Date</label>
                      <input className="form-input" type="date" value={vacStart} onChange={e => { setVacStart(e.target.value); setVacBlackouts([]); }} required />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">End Date</label>
                      <input className="form-input" type="date" value={vacEnd} min={vacStart} onChange={e => { setVacEnd(e.target.value); setVacBlackouts([]); }} required />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">What will they have access to?</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {EQUIPMENT_OPTIONS.map(opt => (
                        <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={vacEquipment.includes(opt.key)}
                            onChange={() => setVacEquipment(prev => prev.includes(opt.key) ? prev.filter(k => k !== opt.key) : [...prev, opt.key])} />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {rangeForForm.length > 1 && (
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label className="form-label">Days they can't run at all</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                        {rangeForForm.map(dateStr => (
                          <label key={dateStr} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                            <input type="checkbox" checked={vacBlackouts.includes(dateStr)}
                              onChange={() => setVacBlackouts(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr])} />
                            {fmtVacDate(dateStr)}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Notes (optional)</label>
                    <input className="form-input" type="text" value={vacNotes} onChange={e => setVacNotes(e.target.value)} placeholder="Hotel gym only, no long runs, etc." />
                  </div>

                  {vacError && <p className="form-error" style={{ marginTop: 0, marginBottom: 8 }}>{vacError}</p>}
                  <button className="btn btn-primary" type="submit" disabled={vacSaving} style={{ width: 'auto', padding: '8px 20px' }}>
                    {vacSaving ? 'Saving...' : 'Save Trip'}
                  </button>
                </form>
              </div>
            )}

            {vacations.length === 0 && !showVacForm ? (
              <div className="section-card-body" style={{ color: 'var(--gray-400)', fontSize: 13 }}>
                No upcoming trips scheduled.
              </div>
            ) : (
              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {vacations.map(v => {
                  const equipLabels = (v.available_equipment ?? []).map(k => EQUIPMENT_OPTIONS.find(o => o.key === k)?.label ?? k);
                  const blackoutLabels = (v.blackout_dates ?? []).sort().map(fmtVacDate);
                  const isSingleDay = v.start_date === v.end_date;
                  const startD = new Date(v.start_date + 'T00:00:00');
                  const endD = new Date(v.end_date + 'T00:00:00');
                  const nights = Math.round((endD - startD) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={v.id} style={{
                      display: 'flex', alignItems: 'stretch', gap: 0,
                      borderRadius: 'var(--radius-sm)', overflow: 'hidden',
                      border: '1px solid var(--gray-200)', background: 'var(--white)',
                    }}>
                      {/* Date badge */}
                      <div style={{
                        background: 'var(--navy)', color: 'var(--white)',
                        minWidth: 58, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        padding: '10px 8px', flexShrink: 0, gap: 1,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.7 }}>
                          {startD.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>
                          {startD.getDate()}
                        </div>
                        {!isSingleDay && (
                          <>
                            <div style={{ fontSize: 10, opacity: 0.6, margin: '2px 0' }}>–</div>
                            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.7 }}>
                              {endD.toLocaleDateString('en-US', { month: 'short' })}
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
                              {endD.getDate()}
                            </div>
                          </>
                        )}
                      </div>
                      {/* Event body */}
                      <div style={{ flex: 1, padding: '10px 14px', minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 3 }}>
                          {v.title || 'Trip'}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: equipLabels.length || blackoutLabels.length || v.notes ? 6 : 0 }}>
                          {isSingleDay
                            ? fmtVacDate(v.start_date)
                            : `${fmtVacDate(v.start_date)} – ${fmtVacDate(v.end_date)}${nights > 0 ? ` · ${nights} night${nights !== 1 ? 's' : ''}` : ''}`
                          }
                        </div>
                        {equipLabels.length > 0 && (
                          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>
                            <span style={{ fontWeight: 600 }}>Available: </span>{equipLabels.join(', ')}
                          </div>
                        )}
                        {blackoutLabels.length > 0 && (
                          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>
                            <span style={{ fontWeight: 600 }}>No-run days: </span>{blackoutLabels.join(', ')}
                          </div>
                        )}
                        {v.notes && (
                          <div style={{ fontSize: 12, color: 'var(--gray-400)', fontStyle: 'italic', marginTop: 2 }}>"{v.notes}"</div>
                        )}
                      </div>
                      <button onClick={() => handleDeleteVacation(v.id)} title="Remove"
                        style={{ background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 18, padding: '0 12px', flexShrink: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--gray-500)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-300)'}
                      >&times;</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Day Detail Modal */}
      {logViewDay && !logEditDay && (
        <div className="tl-modal-overlay" onClick={() => setLogViewDay(null)}>
          <div className="tl-modal tl-log-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="tl-modal-header">
              <div>
                <div className="tl-modal-title">
                  {new Date(logViewDay.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </div>
                {logViewDay.log?.total_miles > 0 && (
                  <div className="tl-modal-step-count">
                    {(logViewDay.log.running_miles || 0) > 0.001 && (logViewDay.log.xt_miles || 0) > 0.001
                      ? `Run: ${logViewDay.log.running_miles.toFixed(2)} mi · XT: ${logViewDay.log.xt_miles.toFixed(2)} mi · Total: ${logViewDay.log.total_miles.toFixed(2)} mi`
                      : (logViewDay.log.xt_miles || 0) > 0.001 && (logViewDay.log.running_miles || 0) <= 0.001
                        ? `XT: ${logViewDay.log.xt_miles.toFixed(2)} mi`
                        : `${logViewDay.log.total_miles.toFixed(2)} mi logged`}
                  </div>
                )}
              </div>
              <button className="day-modal-close" type="button" onClick={() => setLogViewDay(null)}>×</button>
            </div>

            <div className="tl-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {/* Add-flag affordance — always visible so the coach
                  can log on the athlete's behalf even when nothing
                  has been reported. */}
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn-xs btn-xs-ghost"
                  onClick={() => setCreatingFlagDate(logViewDay.date)}
                  style={{ fontSize: 12, fontWeight: 600 }}
                >
                  + Log injury or illness
                </button>
              </div>
              {/* Habits — render as green (done) / red (missed)
                  for habits whose days_of_week includes this day. */}
              {(() => {
                const dateStr = logViewDay.date;
                const dayOfWeek = new Date(dateStr + 'T00:00:00').getDay();
                const today = new Date().toISOString().slice(0, 10);
                const todaysHabits = habits.filter(h =>
                  (h.days_of_week ?? []).includes(dayOfWeek)
                );
                if (todaysHabits.length === 0) return null;
                const completed = habitCompletionsByDate[dateStr] ?? new Set();
                return (
                  <div style={{
                    background: 'var(--gray-50)',
                    border: '1px solid var(--gray-100)',
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginBottom: 12,
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
                      color: 'var(--gray-500)', textTransform: 'uppercase',
                      marginBottom: 6,
                    }}>
                      Habits
                    </div>
                    {todaysHabits.map(h => {
                      const done = completed.has(h.id);
                      // Future days haven't happened yet — render
                      // them muted instead of red.
                      const isFuture = dateStr > today;
                      const color = done
                        ? '#15803d'
                        : isFuture ? 'var(--gray-400)' : '#b91c1c';
                      return (
                        <div key={h.id} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          fontSize: 13, color, fontWeight: 600,
                          padding: '2px 0',
                        }}>
                          <span style={{ fontSize: 14 }}>{done ? '✓' : isFuture ? '·' : '✗'}</span>
                          <span>{h.name}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Health flags */}
              {(logViewDay.flags ?? []).length > 0 && (
                <div className="tl-log-flag-section">
                  {(logViewDay.flags ?? []).map(f => (
                    <div key={f.id} className="tl-log-flag-row">
                      <div className="tl-log-flag-icon">{f.type === 'injury' ? '🩹' : '🤒'}</div>
                      <div className="tl-log-flag-info">
                        <div className="tl-log-flag-type">
                          {f.type === 'injury' ? 'Injury' : 'Illness'}
                          {f.type === 'injury' && f.body_part ? ` — ${f.body_part}` : ''}
                          {f.type === 'illness' && f.illness_type ? ` — ${f.illness_type}` : ''}
                          {f.type === 'injury' && f.pain_scale != null ? ` (pain ${f.pain_scale}/10)` : ''}
                        </div>
                        {f.notes && <div className="tl-log-flag-notes">{f.notes}</div>}
                        <div className="tl-log-flag-meta">
                          Started {new Date(f.flag_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {f.resolved && f.resolved_at
                            ? ` · Resolved ${new Date(f.resolved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            : ' · Active'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                        <button
                          className="btn-xs btn-xs-ghost"
                          style={{ fontSize: 11 }}
                          onClick={() => setEditingFlag(f)}
                        >
                          Edit
                        </button>
                        {!f.resolved && (
                          <button
                            className="btn-xs btn-xs-ghost"
                            style={{ fontSize: 11 }}
                            onClick={() => markFlagResolved(f.id)}
                          >
                            Mark Resolved
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Planned workout */}
              {(logViewDay.planned?.text || logViewDay.planned?.athleteRest || logViewDay.planned?.swappedFromDate) && (
                <div className="tl-log-planned-section">
                  <div className="tl-log-section-label">
                    Planned
                    {logViewDay.planned?.swappedFromDate && (
                      <span className="day-swapped-badge" style={{ marginLeft: 8 }}>↔ Moved</span>
                    )}
                    {logViewDay.planned?.athleteRest && (
                      <span className="day-rest-badge" style={{ marginLeft: 8 }}>Rest Day</span>
                    )}
                  </div>
                  {logViewDay.planned?.text && (
                    <div className="tl-log-planned-text">{renderWorkoutText(resolvePaceTags(logViewDay.planned.text, applyAltitudeAdjustment(paces, altitudeFt)))}</div>
                  )}
                </div>
              )}

              {/* Logged segments */}
              {logViewDay.log ? (
                <div className="tl-log-logged-section">
                  <div className="tl-log-section-label">Logged</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(logViewDay.log.segments ?? []).map((seg, i) => {
                      const pace = avgPaceFromSeg(seg);
                      const prevSeg = logViewDay.log.segments[i - 1];
                      const showSessionBadge = seg.session && seg.session !== prevSeg?.session;
                      return (
                        <div key={i} className="tl-coach-seg">
                          {showSessionBadge && (
                            <div className={`tl-session-badge tl-session-badge--${seg.session}`} style={{ marginBottom: 6 }}>
                              {seg.session.toUpperCase()} Session
                            </div>
                          )}
                          <div className="tl-coach-seg-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{seg.label || seg.type}</span>
                            {pace && <span style={{ fontWeight: 400, color: 'var(--gray-500)', textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>{pace}</span>}
                          </div>
                          {(seg.type === 'interval' || seg.type === 'timed_interval') && (
                            <div className="tl-coach-seg-splits">
                              {(seg.splits ?? []).map((sp, j) => sp ? (
                                <span key={j} className="tl-coach-split-chip">
                                  {j + 1}. {sp}
                                  {seg.rests?.[j] && <span style={{ color: 'var(--gray-400)' }}> / {seg.rests[j]}</span>}
                                </span>
                              ) : null).filter(Boolean)}
                            </div>
                          )}
                          {seg.type === 'continuous' && (
                            <div className="tl-coach-seg-detail">
                              {seg.distance} {seg.distUnit}{seg.time ? ` — ${seg.time}` : ''}
                            </div>
                          )}
                          {seg.type === 'xt' && seg.duration && (
                            <div className="tl-coach-seg-detail">{seg.duration}</div>
                          )}
                          {seg.type === 'checkbox' && (
                            <div className="tl-coach-seg-detail" style={{ color: seg.checked ? '#16a34a' : 'var(--gray-400)' }}>
                              {seg.checked ? '✓ Completed' : 'Not completed'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {logViewDay.log.notes && (
                      <div className="tl-coach-log-notes">"{logViewDay.log.notes}"</div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ padding: '12px 0', color: 'var(--gray-400)', fontSize: 13, fontStyle: 'italic' }}>
                  No log entry for this day.
                </div>
              )}
            </div>

            <div className="day-modal-footer">
              <button className="btn-xs btn-xs-ghost" onClick={() => setLogViewDay(null)}>Close</button>
              {logViewDay.log && (() => {
                const dayDate = logViewDay.date;
                const hasAm = !!monthLogSessions[dayDate]?.am;
                const hasPm = !!monthLogSessions[dayDate]?.pm;
                const isAmPmDay = hasAm || hasPm;
                if (isAmPmDay) {
                  return (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {hasAm && (
                        <button
                          className="btn-xs btn-xs-ghost"
                          style={{ color: '#b91c1c' }}
                          onClick={() => {
                            if (window.confirm('Clear the AM log? This cannot be undone.')) {
                              handleDeleteLog(dayDate, 'am');
                            }
                          }}
                        >
                          Clear AM
                        </button>
                      )}
                      {hasPm && (
                        <button
                          className="btn-xs btn-xs-ghost"
                          style={{ color: '#b91c1c' }}
                          onClick={() => {
                            if (window.confirm('Clear the PM log? This cannot be undone.')) {
                              handleDeleteLog(dayDate, 'pm');
                            }
                          }}
                        >
                          Clear PM
                        </button>
                      )}
                      {hasAm && hasPm && (
                        <button
                          className="btn-xs btn-xs-ghost"
                          style={{ color: '#b91c1c' }}
                          onClick={() => {
                            if (window.confirm('Clear the entire day log (AM + PM)? This cannot be undone.')) {
                              handleDeleteLog(dayDate, null);
                            }
                          }}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    className="btn-xs btn-xs-ghost"
                    style={{ color: '#b91c1c' }}
                    onClick={() => {
                      if (window.confirm('Clear this log entry? The day will reset to the prescribed workout.')) {
                        handleDeleteLog(dayDate, null);
                      }
                    }}
                  >
                    Clear Log
                  </button>
                );
              })()}
              {(() => {
                const planned = logViewDay.planned?.text ?? '';
                const isAmPm = /\bam\s*:/i.test(planned) && /\bpm\s*:/i.test(planned);
                const dayDate = logViewDay.date;
                const d = new Date(dayDate + 'T00:00:00');
                const makeEditDay = (sess) => ({
                  date: dayDate,
                  dayLabel: d.toLocaleDateString('en-US', { weekday: 'short' }),
                  dayNum: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                  rawWorkout: planned,
                  startSession: sess,
                });
                if (isAmPm) {
                  const amLogged = !!monthLogSessions[dayDate]?.am;
                  const pmLogged = !!monthLogSessions[dayDate]?.pm;
                  return (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-xs btn-xs-primary" onClick={() => { setLogEditDay(makeEditDay('am')); setLogViewDay(null); }}>
                        {amLogged ? '✓ Edit AM' : '+ Log AM'}
                      </button>
                      <button className="btn-xs btn-xs-primary" style={pmLogged ? {} : { opacity: 0.75 }} onClick={() => { setLogEditDay(makeEditDay('pm')); setLogViewDay(null); }}>
                        {pmLogged ? '✓ Edit PM' : '+ Log PM'}
                      </button>
                    </div>
                  );
                }
                return (
                  <button className="btn-xs btn-xs-primary" onClick={() => { setLogEditDay(makeEditDay(null)); setLogViewDay(null); }}>
                    {logViewDay.log ? 'Edit Log' : '+ Log for Athlete'}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Coach Training Log Entry Modal */}
      {logEditDay && (
        <TrainingLogModal
          day={logEditDay}
          existingLog={monthLogSessions[logEditDay.date]?.full ?? null}
          existingAmLog={monthLogSessions[logEditDay.date]?.am ?? null}
          existingPmLog={monthLogSessions[logEditDay.date]?.pm ?? null}
          startSession={logEditDay.startSession}
          initialAthleteRest={planWorkouts[logEditDay.date]?.athleteRest ?? false}
          athleteId={id}
          paces={applyAltitudeAdjustment(paces, altitudeFt)}
          onSave={handleCoachLogSaved}
          onRestDay={handleCoachRestDay}
          onClose={() => setLogEditDay(null)}
        />
      )}

      {(editingFlag || creatingFlagDate) && (
        <CoachHealthFlagModal
          flag={editingFlag}
          athleteId={id}
          defaultDate={creatingFlagDate}
          onClose={() => { setEditingFlag(null); setCreatingFlagDate(null); }}
          // Both save and delete refresh the month-flag list; the
          // useEffect above resyncs the open day modal so the row
          // updates / disappears / appears in place.
          onSaved={() => fetchMonthFlags()}
          onDeleted={() => fetchMonthFlags()}
        />
      )}

    </Layout>
  );
}

// ── Habit helpers (shared with coach habit modal below) ──────────
const DAY_LABELS = [
  { value: 0, label: 'Sun' }, { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function describeDays(days = []) {
  if (!days.length) return 'Never';
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 7) return 'Every day';
  if (sorted.join(',') === '1,2,3,4,5') return 'Weekdays';
  if (sorted.join(',') === '0,6')        return 'Weekends';
  return sorted.map(d => DAY_LABELS[d].label).join(' · ');
}

/**
 * Coach-side habit editor. Same layout as the mobile HabitsScreen
 * form — name, day chips, optional notes. Save / Delete handled
 * by the parent so it shares the habits refetch + RLS-error
 * surfacing.
 */
function CoachHabitEditModal({ draft, err, saving, onChange, onCancel, onSave }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,24,44,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, padding: 20,
          width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>
            {draft.id ? 'Edit habit' : 'New habit'}
          </h2>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--gray-400)', cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Name</label>
          <input
            type="text"
            className="form-input"
            value={draft.name ?? ''}
            onChange={e => onChange({ name: e.target.value })}
            placeholder="e.g. Drink 3L water"
          />
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Active days</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {DAY_LABELS.map(d => {
              const sel = (draft.days_of_week ?? []).includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => {
                    const cur = draft.days_of_week ?? [];
                    const next = cur.includes(d.value)
                      ? cur.filter(x => x !== d.value)
                      : [...cur, d.value].sort((a, b) => a - b);
                    onChange({ days_of_week: next });
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: '1.5px solid',
                    borderColor: sel ? 'var(--navy)' : 'var(--gray-200)',
                    background: sel ? 'var(--navy)' : '#fff',
                    color: sel ? '#fff' : 'var(--gray-700)',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { label: 'Every day', days: [0, 1, 2, 3, 4, 5, 6] },
              { label: 'Weekdays', days: [1, 2, 3, 4, 5] },
              { label: 'Weekends', days: [0, 6] },
            ].map(s => (
              <button
                key={s.label}
                type="button"
                onClick={() => onChange({ days_of_week: s.days })}
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--gray-200)',
                  background: '#fff',
                  fontSize: 11, fontWeight: 600,
                  color: 'var(--gray-700)',
                  cursor: 'pointer',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Notes (optional)</label>
          <textarea
            className="form-input"
            rows={2}
            value={draft.notes ?? ''}
            onChange={e => onChange({ notes: e.target.value })}
            placeholder="Anything specific about this habit?"
          />
        </div>

        {err ? <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{err}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-xs btn-xs-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving}
            style={{ width: 'auto', padding: '8px 16px' }}
          >
            {saving ? 'Saving…' : draft.id ? 'Save' : 'Add habit'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Coach-side goal editor modal. Mirrors the mobile GoalsScreen
 * form: event chip picker, goal-time input (free text — accepts
 * "3:55.00", "15:30", "2:30:45", etc., parsed via parseTimeToSeconds),
 * optional notes. Save / Delete logic lives on the parent so it
 * shares the goals refetch + RLS-error surfacing.
 */
function CoachGoalEditModal({ draft, existingEvents, err, saving, onChange, onCancel, onSave }) {
  const eventKeys = Object.entries(EVENTS)
    .filter(([k]) => !isRelayEvent(k))
    .sort(([, a], [, b]) => (a.meters ?? Infinity) - (b.meters ?? Infinity))
    .map(([k]) => k);
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,24,44,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, padding: 20,
          width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>
            {draft.id ? 'Edit goal' : 'New goal'}
          </h2>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--gray-400)', cursor: 'pointer', padding: 0 }}
            aria-label="Close"
          >×</button>
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Event</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {eventKeys.map(key => {
              const selected = draft.event === key;
              const disabled = existingEvents.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => !disabled && onChange({ event: key })}
                  disabled={disabled}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 999,
                    border: '1.5px solid',
                    borderColor: selected ? 'var(--navy)' : 'var(--gray-200)',
                    background: selected ? 'var(--navy)' : '#fff',
                    color: selected ? '#fff' : 'var(--gray-700)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.4 : 1,
                  }}
                  title={disabled ? 'Active goal already exists for this event' : ''}
                >
                  {EVENTS[key].label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Goal time</label>
          <input
            type="text"
            className="form-input"
            value={draft.goal_time_str ?? ''}
            onChange={e => onChange({ goal_time_str: e.target.value })}
            placeholder="3:55.00"
            style={{ fontFamily: 'Menlo, monospace' }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Notes (optional)</label>
          <textarea
            className="form-input"
            rows={2}
            value={draft.notes ?? ''}
            onChange={e => onChange({ notes: e.target.value })}
            placeholder="e.g. State meet target"
          />
        </div>

        {err ? <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{err}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-xs btn-xs-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving}
            style={{ width: 'auto', padding: '8px 16px' }}
          >
            {saving ? 'Saving…' : draft.id ? 'Save' : 'Add goal'}
          </button>
        </div>
      </div>
    </div>
  );
}
